import { useEffect, useState, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import JobRequestSimplified from './JobRequestSimplified';
import JobRequestNew from './JobRequestNew';
import JobRequestSuccess from './JobRequestSuccess';
import { V2JobRequest, V2JobRequestSuccess } from './v2/V2JobRequest';
import V2Home from './v2/V2Home';
import { V2About, V2Services, V2AreasPricing, V2Notary, V2FAQ, V2Contact, V2NotFound } from './v2/V2Pages';
import { V2Privacy, V2Terms } from './v2/V2Legal';
import V2PaymentSuccess from './v2/V2PaymentSuccess';
import { HelmetProvider } from 'react-helmet-async';

// Portal V2 (canonical — V2 lives at /portal/*; legacy V1 portal pages removed June 2026)
import {
  PortalV2Login,
  PortalV2Register,
  PortalV2ResetPassword,
  AdminV2Dashboard,
  ClientV2Dashboard,
  JobV2Detail,
  ClientV2Jobs,
  ClientV2Activity,
  ClientV2Invoices,
  ClientV2InvoiceDetail,
  ClientV2SubmitJob,
  ClientV2GuestPayment,
  ClientV2StatementPayment,
  ClientV2Settings,
  AdminV2Jobs,
  AdminV2JobsMap,
  AdminV2Invoices,
  AdminV2Clients,
  AdminV2Domains,
  AdminV2Referrals,
  AdminV2NotaryBookings,
  AdminV2ServerFinder,
  AdminV2ServerJobs,
} from './portal-v2';
import { PortalV2LegacyRedirect, isPreviewEnv } from './portal-v2/PreviewPortalRedirect';

// Notary Booking System
import NotaryBooking, { NotaryBookingWidget } from './NotaryBooking';
import NotaryConfirmation from './NotaryConfirmation';
import NotaryManageBooking from './NotaryManageBooking';

// Client Portal imports
import { AuthProvider } from './contexts/AuthContext';
// ProtectedRoute is the shared auth guard used by all canonical V2 portal routes.
import { ProtectedRoute } from './portal';

// V2 Preview Site (parallel version — original site untouched)
import V2Routes from './v2';


// Google Analytics setup
const initializeGA = () => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
  
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'GA_MEASUREMENT_ID') {
    // Initialize gtag if not already loaded
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(){window.dataLayer.push(arguments);};
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID);
      
      // Load GA script if not already loaded
      if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);
      }
    }
  }
};

// Track page views
const trackPageView = (url) => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (window.gtag && GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'GA_MEASUREMENT_ID') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// Update canonical URL for SEO
const updateCanonical = (path) => {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.href = `https://westexlegal.com${path}`;
  }
  
  // Add robots meta tag for redirect pages
  let robotsTag = document.querySelector('meta[name="robots"]');
  if (!robotsTag) {
    robotsTag = document.createElement('meta');
    robotsTag.name = 'robots';
    document.head.appendChild(robotsTag);
  }
  
  // Don't index redirect pages
  if (path === '/pricing' || path === '/contact-us' || path === '/notaryty') {
    robotsTag.content = 'noindex, nofollow';
  } else {
    robotsTag.content = 'index, follow';
  }
};

// Backend URL - properly configured
// API base - always use relative URLs so it works on any domain (preview or production)
const API = '/api';

// Assets
const LOGO = "/westex-logo.png";
const NAPPS_BADGE = "https://customer-assets.emergentagent.com/job_legal-site-update/artifacts/lxkv94qi_NAPPS4.png";

const PHONE = "8064011749"; // display only

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-7xl px-6 sm:px-8 ${className}`}>{children}</div>
);

const Button = ({ children, to, onClick, href, variant = "primary", className = "" }) => {
  const baseCls =
    variant === "primary"
      ? "btn-primary"
      : variant === "outline-invert"
      ? "btn-outline-invert"
      : variant === "secondary"
      ? "btn-secondary"
      : "btn-outline";
  const cls = `${baseCls} ${className}`.trim();
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  if (href) return <a href={href} className={cls}>{children}</a>;
  return (
    <button onClick={onClick} className={cls}>
      {children}
    </button>
  );
};

// Micro animations with IntersectionObserver
const useRevealOnScroll = () => {
  useEffect(() => {
    // Enable JavaScript-enhanced animations
    document.body.classList.add('js-enabled');
    
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05 }, // Lowered from 0.15 for better detection
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

// Simple page fade-in on route change with scroll to top
const Page = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Scroll to top when route changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Track page view for Google Analytics
    trackPageView(location.pathname);
    
    // Update canonical URL for SEO
    updateCanonical(location.pathname);
  }, [location.pathname]);
  
  return (
    <div key={location.pathname} className="page-fade">
      {children}
    </div>
  );
};

// Scroll progress bar
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = (h.scrollTop) / (h.scrollHeight - h.clientHeight);
      setProgress(isNaN(scrolled) ? 0 : scrolled);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="progress-bar" style={{ width: `${progress * 100}%` }} />;
};

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const linkClass = ({ isActive }) => `nav-link ${isActive ? "active" : ""}`;

  // Payment Modal Component
  const PaymentModal = () => {
    const [referenceId, setReferenceId] = useState('');
    const [amount, setAmount] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');



    const handlePayment = async () => {
      // Validate inputs before processing
      if (!referenceId || !referenceId.trim()) {
        setError('Please enter a reference ID');
        return;
      }
      
      if (!amount || !amount.trim()) {
        setError('Please enter an amount');
        return;
      }
      
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        setError('Please enter a valid amount');
        return;
      }
      
      if (numAmount < 10) {
        setError('Minimum payment amount is $10.00');
        return;
      }
      
      if (numAmount > 2000) {
        setError('Maximum payment amount is $2,000.00');
        return;
      }

      setIsProcessing(true);
      setError('');

      try {
        console.log('Making payment request to:', `/api/payments/checkout/session`);
        console.log('Request data:', { reference_id: referenceId.trim(), amount: numAmount, origin_url: window.location.origin });
        
        const response = await axios.post(`/api/payments/checkout/session`, {
          reference_id: referenceId.trim(),
          amount: numAmount,
          origin_url: window.location.origin
        }, {
          timeout: 30000, // 30 second timeout - increased
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: false // Ensure CORS compatibility
        });

        console.log('Payment response:', response.data);
        
        // Redirect to Stripe Checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('No checkout URL received from server');
        }
      } catch (err) {
        console.error('Payment error details:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);
        
        setIsProcessing(false);
        
        if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please check your connection and try again.');
        } else if (err.response?.data?.detail) {
          setError(err.response.data.detail);
        } else if (err.response?.status) {
          setError(`Server error (${err.response.status}). Please try again.`);
        } else if (err.message.includes('Network Error')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError('Failed to create payment session. Please try again.');
        }
      }
    };

    const handleAmountChange = (e) => {
      const value = e.target.value;
      // Allow only numbers and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setAmount(value);
      }
    };

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] ${showPaymentModal ? 'block' : 'hidden'}`}>
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Pay Invoice</h3>
            <button 
              onClick={() => {
                setShowPaymentModal(false);
                setReferenceId('');
                setAmount('');
                setError('');
              }}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference ID / Job Number:
              </label>
              <input
                type="text"
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                placeholder="Enter your job reference number"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] outline-none text-gray-900 placeholder-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Amount:
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] outline-none text-gray-900 placeholder-gray-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum: $10.00 • Maximum: $2,000.00</p>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setReferenceId('');
                setAmount('');
                setError('');
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 bg-[hsl(357,85%,52%)] text-white rounded-lg hover:bg-[hsl(357,85%,47%)] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? 'Processing...' : 'Pay with Stripe'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-[70] w-full border-b border-white/10 dark-section">
      <ScrollProgress />
      <Container className="relative h-16 flex items-center justify-center">
        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden absolute left-0 inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/20 text-white"
          aria-label="Toggle navigation"
        >
          <span className="i-lucide-menu">☰</span>
        </button>
        <nav className="hidden md:flex items-center gap-2">
          <NavLink to="/" className="nav-button" end>
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
              <span>Home</span>
            </span>
          </NavLink>
          <NavLink to="/about" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
              <span>About</span>
            </span>
          </NavLink>
          <NavLink to="/services" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
              <span>Services</span>
            </span>
          </NavLink>
          <NavLink to="/areas-pricing" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              <span>Areas & Pricing</span>
            </span>
          </NavLink>
          <NavLink to="/notary" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>
              <span>Notary</span>
            </span>
          </NavLink>
          <NavLink to="/faq" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
              <span>FAQ</span>
            </span>
          </NavLink>
          <NavLink to="/contact" className="nav-button">
            <span className="nav-button-content">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
              <span>Contact</span>
            </span>
          </NavLink>
        </nav>
        {/* Desktop buttons */}
        <div className="hidden md:flex absolute -right-8 gap-3 items-center">
          <a 
            href="tel:8064011749" 
            className="px-3 py-1.5 bg-[hsl(357,85%,52%)] text-white rounded-md font-medium text-xs hover:bg-[hsl(357,85%,47%)] transition-all duration-200 shadow-sm flex items-center"
          >
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            (806) 401-1749
          </a>
          <button 
            onClick={() => setShowPaymentModal(true)}
            className="px-3 py-1.5 bg-green-600 text-white rounded-md font-medium text-xs hover:bg-green-700 transition-all duration-200 shadow-sm flex items-center"
          >
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2v2H4V6h16zm0 6H4v6h16v-6z"/>
            </svg>
            Pay Invoice
          </button>
          <Link 
            to="/portal/login"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md font-medium text-xs hover:bg-blue-700 transition-all duration-200 shadow-sm flex items-center"
            title="Client Portal"
          >
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
            </svg>
            Portal
          </Link>
        </div>

        {/* Mobile buttons - using onClick for both since Pay Invoice onClick works */}
        <div className="flex md:hidden fixed top-2 right-2 gap-2 items-center z-50">
          <button
            onClick={() => {
              console.log('Call button clicked');
              window.open('tel:8064011749', '_self');
            }}
            className="px-3 py-2 bg-[hsl(357,85%,52%)] text-white rounded-lg font-medium text-sm hover:bg-[hsl(357,85%,47%)] transition-all duration-200 shadow-lg flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            Call
          </button>
          <button 
            onClick={() => setShowPaymentModal(true)}
            className="px-3 py-2 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 transition-all duration-200 shadow-lg flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2v2H4V6h16zm0 6H4v6h16v-6z"/>
            </svg>
            Pay Invoice
          </button>
        </div>
      </Container>
      {open && (
        <div className="border-t border-white/10 md:hidden">
          <Container className="py-3 grid grid-cols-2 gap-2 text-white">
            <NavLink to="/" className="nav-button-mobile" onClick={() => setOpen(false)} end>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                <span>Home</span>
              </span>
            </NavLink>
            <NavLink to="/about" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
                <span>About</span>
              </span>
            </NavLink>
            <NavLink to="/services" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
                <span>Services</span>
              </span>
            </NavLink>
            <NavLink to="/areas-pricing" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                <span>Areas & Pricing</span>
              </span>
            </NavLink>
            <NavLink to="/notary" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>
                <span>Notary</span>
              </span>
            </NavLink>
            <NavLink to="/faq" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
                <span>FAQ</span>
              </span>
            </NavLink>
            <NavLink to="/contact" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                <span>Contact</span>
              </span>
            </NavLink>
            <NavLink to="/portal/login" className="nav-button-mobile" onClick={() => setOpen(false)}>
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
                <span>Client Portal</span>
              </span>
            </NavLink>
            <a href={`tel:${PHONE}`} className="nav-button-mobile col-span-2 justify-center">
              <span className="nav-button-content">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                <span>(806) 401-1749</span>
              </span>
            </a>
          </Container>
        </div>
      )}
      <PaymentModal />
    </header>
  );
};

const Hero = () => (
  <section className="black-section pattern-dots relative overflow-hidden">
    {/* Animated subtle orbs */}
    <div className="orb bg-[hsl(var(--primary)/0.08)] w-32 h-32 -top-5 -left-5 absolute" />
    <div className="orb delay bg-[hsl(var(--primary)/0.07)] w-36 h-36 bottom-0 right-5 absolute" />

    <Container className="py-6 text-center text-white">
      <div className="logo-entrance" style={{ transitionDelay: '.05s' }}>
        <img src={LOGO} alt="WesTex Legal" className="mx-auto h-24 w-auto md:h-28 lg:h-32" />
      </div>
      <div className="reveal mt-3" style={{ transitionDelay: '.15s' }}>
        <div className="flex flex-col items-center justify-center">
          <Button to="/job-request" className="text-sm px-5 py-2">Submit a Job</Button>
        </div>
      </div>
    </Container>
  </section>
);

export const HowItWorks = () => (
  <section className="black-section">
    <Container className="py-16 text-center">
      <h2 className="heading-accent font-serif-head text-3xl font-bold text-white">How It Works</h2>
      <p className="mt-2 text-white/90">Our streamlined process makes legal service delivery simple and efficient.</p>
      <div className="mt-8 grid gap-8 md:grid-cols-4">
        {[
          { 
            step: "1", 
            title: "Send Documents", 
            text: "Submit your documents and service details through our secure system or by email.",
            link: "/job-request"
          },
          { 
            step: "2", 
            title: "We Attempt & Update", 
            text: "Our certified process servers attempt service and provide real-time updates via our case management system."
          },
          { 
            step: "3", 
            title: "Proof of Service", 
            text: "Receive completed affidavit of service and any required documentation promptly upon successful service."
          },
          { 
            step: "4", 
            title: "Access Your Portal", 
            text: "Log into your Client Portal anytime to view job status, download affidavits, and track all your cases.",
            link: "/portal/login",
            linkText: "Access Portal →"
          }
        ].map((item, i) => (
          item.link ? (
            <Link key={item.step} to={item.link} className="gradient-outline-white p-6 text-center reveal shadow-lg hover:shadow-xl transition-all cursor-pointer block" style={{ transitionDelay: `${0.2 + i * 0.15}s` }}>
              <div className="mx-auto w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">
                {item.step}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-700 text-sm">{item.text}</p>
              <div className="mt-3 text-red-600 font-semibold text-sm">{item.linkText || "Click to submit →"}</div>
            </Link>
          ) : (
            <div key={item.step} className="gradient-outline-white p-6 text-center reveal shadow-lg" style={{ transitionDelay: `${0.2 + i * 0.15}s` }}>
              <div className="mx-auto w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">
                {item.step}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-700 text-sm">{item.text}</p>
            </div>
          )
        ))}
      </div>
      
      {/* Client Portal Info Banner */}
      <div className="mt-12 max-w-3xl mx-auto reveal" style={{ transitionDelay: '0.8s' }}>
        <Link 
          to="/portal/login" 
          className="block bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all group"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="bg-[hsl(357,85%,52%)] text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
            <span className="font-semibold text-white text-lg">Client Portal Now Available</span>
          </div>
          <p className="text-white/90">
            Track your jobs in real-time, view attempt details, and download affidavits — all in one place.
          </p>
          <p className="text-white/70 text-sm mt-2">
            <strong className="text-white">No change to your workflow:</strong> Continue submitting jobs via email as usual. 
            Once we enter your job, it automatically appears in your portal for tracking.
          </p>
          <span className="text-[hsl(357,85%,52%)] mt-3 inline-block font-semibold group-hover:underline">
            Access Your Portal →
          </span>
        </Link>
      </div>
    </Container>
  </section>
);

export const Affiliations = () => (
  <section className="black-section py-12">
    <Container>
      <div className="text-center reveal">
        <h3 className="font-semibold text-lg text-white mb-6">Professional Affiliations</h3>
        <div className="flex justify-center items-center">
          <div className="flex flex-col items-center gap-2">
            <img src={NAPPS_BADGE} alt="NAPPS - National Association of Professional Process Servers" className="h-16 w-auto" />
            <span className="text-xs text-white/60">NAPPS Member</span>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

const StickyMobileCallButton = () => (
  <a 
    href={`tel:${PHONE}`}
    className="fixed bottom-6 right-6 z-50 sm:hidden bg-[hsl(357,85%,52%)] text-white p-4 rounded-full shadow-lg hover:bg-[hsl(357,85%,47%)] transition-colors duration-200 flex items-center justify-center"
    aria-label="Call WesTex Legal"
  >
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
    </svg>
  </a>
);

// Google Reviews with live data from API
export const GoogleReviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Client-side hydration fix
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Fallback reviews if API fails
  const fallbackReviews = [
    { author_name: "D. G. (Big D)", relative_time_description: "5 months ago", text: "Should have called sooner—quick, professional, and on point.", rating: 5 },
    { author_name: "Linda Day", relative_time_description: "4 months ago", text: "I sent the documents and they handled the rest the same day we spoke.", rating: 5 },
    { author_name: "Fran Green", relative_time_description: "2 months ago", text: "Great service and flexible to our needs.", rating: 5 }
  ];

  // Fetch reviews from API
  useEffect(() => {
    if (!isClient) return;
    
    const fetchGoogleReviews = async () => {
      try {
        const response = await fetch(`/api/reviews/google`);
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Use Google Reviews data
            setReviews(data);
          } else {
            // No Google Reviews available, use fallback
            setReviews(fallbackReviews);
          }
        } else {
          // API error, use fallback
          console.warn('Google Reviews API unavailable, using fallback reviews');
          setReviews(fallbackReviews);
        }
      } catch (err) {
        // Network error, use fallback
        console.warn('Failed to fetch Google Reviews:', err.message);
        setReviews(fallbackReviews);
      } finally {
        setLoading(false);
      }
    };

    fetchGoogleReviews();
  }, [isClient]);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (!isClient || loading || reviews.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [reviews.length, isClient, loading]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`text-2xl ${index < rating ? 'text-yellow-500' : 'text-gray-300'}`}
      >
        ★
      </span>
    ));
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substr(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <section className="black-section">
        <Container className="py-16 text-center">
          <h2 className="heading-accent font-serif-head text-3xl font-bold text-white">What Our Clients Say</h2>
          <p className="mt-2 text-white/90">Loading reviews from Google...</p>
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        </Container>
      </section>
    );
  }

  if (reviews.length === 0) {
    return (
      <section className="black-section">
        <Container className="py-16 text-center">
          <h2 className="heading-accent font-serif-head text-3xl font-bold text-white">What Our Clients Say</h2>
          <p className="mt-2 text-white/90">We're proud to serve clients across West Texas.</p>
          <div className="mt-8 gradient-outline-white p-8 max-w-2xl mx-auto">
            <p className="text-gray-800">Reviews are currently being loaded. Please check back soon!</p>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="black-section">
      <Container className="py-16 text-center">
        <h2 className="heading-accent font-serif-head text-3xl font-bold text-white">What Our Clients Say</h2>
        <p className="mt-2 text-white/90">We're proud to be recommended by clients across West Texas.</p>
        
        {/* Slider Container */}
        <div className="mt-8 relative">
          <div className="overflow-hidden rounded-2xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {reviews.map((review, index) => (
                <div key={`${review.author_name}-${index}`} className="w-full flex-shrink-0 px-4">
                  <figure className="gradient-outline-white p-8 text-center max-w-2xl mx-auto shadow-lg reveal-scale" style={{ transitionDelay: `${index * 0.1}s` }}>
                    <div className=" mb-4">
                      {renderStars(review.rating)}
                    </div>
                    <blockquote className="text-lg text-gray-800 mb-4 italic">
                      "{truncateText(review.text)}"
                    </blockquote>
                    <figcaption className="text-sm font-semibold text-[hsl(357,85%,52%)]">
                      {review.author_name} · {review.relative_time_description}
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls - only show if more than 1 review */}
          {reviews.length > 1 && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[hsl(357,85%,52%)] p-3 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Previous review"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
              
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[hsl(357,85%,52%)] p-3 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Next review"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </button>

              {/* Dot Indicators */}
              <div className="flex justify-center mt-6 gap-2">
                {reviews.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-200 hover:scale-125 ${
                      index === currentIndex 
                        ? 'bg-white scale-110' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to review ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>


      </Container>
    </section>
  );
};

const Divider = () => (
  <div className="w-full">
    <div className="divider" role="separator" aria-hidden="true" />
  </div>
);

// ABOUT
export const About = () => (
  <section className="black-section">
    <Container className="py-16">
      <div className="text-center mb-12">
        <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">About Us</h1>
        <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
        <p className="text-lg text-white/90 max-w-3xl mx-auto">
          Professional process serving with a focus on client satisfaction, advanced technology, 
          and reliable service delivery throughout Texas and New Mexico.
        </p>
      </div>

      {/* Our Approach Section */}
      <div className="mb-16">
        <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.1s' }}>
          <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6 text-center">Our Approach</h2>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg leading-relaxed text-white/90 mb-6">
              We understand that finding a good process server can be tough. Problems range from lousy attitude, 
              poor customer service to being negligent. WesTex Legal provides service of process to Lubbock County 
              and surrounding areas. We also provide Statewide service.
            </p>
            <p className="text-lg leading-relaxed text-white/90 mb-6">
              Our team of legal professionals are experienced and trained to locate any individual. We accomplish 
              this via our advanced skip trace techniques and with our extensive network of Nationwide Professionals, 
              we can serve them anywhere they go.
            </p>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[hsl(357,85%,52%)] text-white rounded-full font-semibold">
              The satisfaction of every client is our number one priority.
            </div>
          </div>
        </div>
      </div>

      {/* Case Management Software Section */}
      <div className="mb-16">
        <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.2s' }}>
          <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6 text-center">Case Management Software</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div>
                <p className="text-lg leading-relaxed text-white/90 mb-4">
                  WesTex Legal utilizes ServeManager, a comprehensive case management system that allows our clients to track 
                  the status of their case in real-time. It provides detailed information on every attempt 
                  and successful serve.
                </p>
                <p className="text-lg leading-relaxed text-white/90">
                  No more having to check the status of your case. All updates are sent automatically directly 
                  to your email.
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex flex-col items-center gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl border border-[hsl(357,85%,52%)]/20">
                  <svg className="w-16 h-16 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                  <div className="text-sm font-semibold text-[hsl(357,85%,52%)]">
                    Real-Time Case Tracking<br/>
                    Automatic Email Updates<br/>
                    Detailed Attempt Reports
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Affiliations Section */}
      <div className="mb-16">
        <div className="gradient-outline p-8 reveal text-center" style={{ transitionDelay: '.3s' }}>
          <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6">Professional Affiliations</h2>
          <div className="max-w-2xl mx-auto">
            <p className="text-lg leading-relaxed text-white/90 mb-6">
              We are a proud member of the National Association of Professional Process Servers (NAPPS). 
              This organization holds a high standard when it comes to being accepted as a member.
            </p>
            <div className="flex justify-center items-center gap-4">
              <img src={NAPPS_BADGE} alt="NAPPS Member" className="h-20 w-auto" />
              <div className="text-left">
                <h3 className="font-bold text-[hsl(357,85%,52%)]">NAPPS Member</h3>
                <p className="text-sm text-white/70">
                  National Association of<br/>
                  Professional Process Servers
                </p>
                <a 
                  href="https://napps.org/ProcessServers.aspx?id=10165" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-[hsl(357,85%,52%)] hover:underline"
                >
                  View NAPPS Profile →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Team Section */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)]">Our Team</h2>
        </div>
        
        <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.4s' }}>
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8 md:grid-cols-3 items-start">
              <div className="md:col-span-1">
                <img 
                  src="/ryan-photo.jpg" 
                  alt="Ryan Crumley - Founder and Owner of WesTex Legal" 
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
              <div className="md:col-span-2">
                <h3 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-4">Ryan Crumley</h3>
                <p className="text-sm text-white/60 mb-4 font-semibold uppercase tracking-wider">
                  Founder & Owner
                </p>
                
                <div className="space-y-4 text-white/90 leading-relaxed">
                  <p>
                    Ryan Crumley is the founder and owner of WesTex Legal. Ryan has a very extensive background 
                    in the legal industry with a combined 15 years in the field.
                  </p>
                  
                  <p>
                    Ryan began his legal career with the Lubbock County District Clerks Office where he did just 
                    about everything. He was the main point of contact on issuance of citations for several years. 
                    He was also heavily involved in handling and helping to implement the E-Filing system that 
                    Lubbock County currently uses today.
                  </p>
                  
                  <p>
                    Upon completing his tenure at the District Clerks Office, Ryan then began to pursue Process Serving 
                    and WesTex Legal was founded and established in August of 2016. In the midst of Process Serving, 
                    and to gain more knowledge, Ryan then began working for a Personal Injury Law Firm, where he was 
                    employed for 2 years.
                  </p>
                  
                  <p className="font-semibold text-[hsl(357,85%,52%)]">
                    Ryan then decided to pursue WesTex Legal as his main career and has been dedicated full-time 
                    to providing exceptional legal services to clients across Texas and New Mexico.
                  </p>
                </div>

                {/* Key Qualifications */}
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-[hsl(357,85%,52%)] rounded-full"></div>
                    <span>15+ Years Legal Experience</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-[hsl(357,85%,52%)] rounded-full"></div>
                    <span>Established August 2016</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-[hsl(357,85%,52%)] rounded-full"></div>
                    <span>E-Filing System Implementation</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-[hsl(357,85%,52%)] rounded-full"></div>
                    <span>NAPPS Professional Member</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center">
        <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.5s' }}>
          <h3 className="text-xl font-bold text-[hsl(357,85%,52%)] mb-4">Ready to Work With Us?</h3>
          <p className="text-white/90 mb-6">
            Experience the difference that professional service, advanced technology, and genuine client care can make.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="tel:8064011749">
              Call (806) 401-1749
            </Button>
            <Button to="/contact" variant="outline">
              Send Message
            </Button>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// SERVICES (icons removed earlier) with animated gradient-outline cards
export const Services = () => {
  const mainServices = [
    { 
      title: "Routine Service", 
      desc: "Up to 4 attempts made.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
    },
    { 
      title: "Rush Service", 
      desc: "Up to 4 attempts made.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/></svg>
    },
    { 
      title: "Background Checks", 
      desc: "We offer background checks, which includes criminal records, evictions, foreclosures, bankruptcies, liens, judgments, current and prior properties, corporate filings, professional licenses, and more.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
    },
    { 
      title: "Document Retrieval", 
      desc: "Courier service for retrieving documents at the courthouse. We also service surrounding counties for a small upgrade fee per county.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/></svg>
    },
    { 
      title: "Skip Tracing", 
      desc: "We offer skip tracing through several databases, which includes current and past addresses, phone numbers, employers, email addresses, relatives, associates and possible associates.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
    },
    { 
      title: "Vehicle Search", 
      desc: "We offer vehicle searches to help determine the current and prior registered owner of a particular vehicle.",
      icon: <svg className="w-8 h-8 text-[hsl(357,85%,52%)]" fill="currentColor" viewBox="0 0 24 24"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
    },
  ];

  const additionalServices = [
    {
      text: "Affidavit Preparation",
      desc: "All types of Affidavits, which include Affidavit of Service, Affidavit of Non Service, Affidavit of Due Diligence (Rule 106), and more.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
    },
    {
      text: "E-Filing",
      desc: "Concierge E-Filing service available. You send me the documents you need E-Filed and I handle the rest.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/></svg>
    },
    {
      text: "Mobile Notary",
      desc: "Traveling mobile notary for notarization of affirmations, affidavits, and more.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"/></svg>
    },
    {
      text: "Nationwide Service",
      desc: "Serving all 50 states.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
    },
    {
      text: "Registered Agent Service",
      desc: "Serving CT Corp, CSC, and more.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
    },
    {
      text: "Service of Process",
      desc: "Residential and Business rates.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z"/></svg>
    },
    {
      text: "Statewide Service",
      desc: "Serving all 254 counties in Texas and all of New Mexico.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22S19,14.25 19,9A7,7 0 0,0 12,2Z"/></svg>
    },
    {
      text: "Subpoena Service",
      desc: "Personal service upon request.",
      icon: <svg className="w-5 h-5 text-[hsl(357,85%,52%)] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/></svg>
    },
  ];

  return (
    <section className="black-section">
      <Container className="py-16">
        <div className="text-center mb-12">
          <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">Services Offered</h1>
          <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
          <p className="text-lg text-white/90 max-w-3xl mx-auto">
            Professional legal services with advanced technology and reliable delivery. 
            We provide comprehensive process serving, notary services, and legal support throughout Texas and New Mexico.
          </p>
        </div>

        {/* Main Services */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center text-[hsl(357,85%,52%)] mb-8 text-white">Core Services</h2>
          <div className="grid gap-6 max-w-4xl mx-auto md:grid-cols-2">
            {mainServices.map((service, idx) => (
              <div key={service.title} className="gradient-outline p-6 reveal" style={{ transitionDelay: `${0.05 + idx * 0.06}s` }}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {service.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[hsl(357,85%,52%)] mb-2">{service.title}</h3>
                    <p className="text-white/90 leading-relaxed">{service.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="my-12 max-w-7xl mx-auto"><Divider /></div>

        {/* Additional Services */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center text-[hsl(357,85%,52%)] mb-8">Additional Services</h2>
          <div className="grid gap-4 max-w-4xl mx-auto md:grid-cols-2">
            {additionalServices.map((item, i) => (
              <div key={item.text} className="gradient-outline p-5 reveal flex items-start gap-3" style={{ transitionDelay: `${0.4 + i * 0.05}s` }}>
                {item.icon}
                <div className="flex-1">
                  <h3 className="font-semibold text-[hsl(357,85%,52%)] mb-1">{item.text}</h3>
                  <p className="text-sm text-white/80">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service Features */}
        <div className="mb-16">
          <div className="gradient-outline p-8 max-w-4xl mx-auto reveal" style={{ transitionDelay: '.6s' }}>
            <h3 className="text-xl font-bold text-[hsl(357,85%,52%)] text-center mb-6">Why Choose WesTex Legal?</h3>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
                  </svg>
                </div>
                <h4 className="font-semibold mb-1">Real-Time Tracking</h4>
                <p className="text-sm text-white/70">Comprehensive case management with automatic email updates</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h4 className="font-semibold mb-1">NAPPS Member</h4>
                <p className="text-sm text-white/70">Professional standards and industry certification</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M16.2,7.8L15,9L13,7L11.8,8.2L13.8,10.2L11.8,12.2L13,13.4L15,11.4L17,13.4L18.2,12.2L16.2,10.2L18.2,8.2L17,7L15,9L16.2,7.8Z"/>
                  </svg>
                </div>
                <h4 className="font-semibold mb-1">24/7 Availability</h4>
                <p className="text-sm text-white/70">Emergency and rush service available when you need it</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[hsl(357,85%,52%)] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22S19,14.25 19,9A7,7 0 0,0 12,2Z"/>
                  </svg>
                </div>
                <h4 className="font-semibold mb-1">Extensive Coverage</h4>
                <p className="text-sm text-white/70">Texas, New Mexico, and nationwide service network</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="gradient-outline p-8 max-w-2xl mx-auto reveal" style={{ transitionDelay: '.7s' }}>
            <h3 className="text-xl font-bold text-[hsl(357,85%,52%)] mb-4">Ready to Get Started?</h3>
            <p className="text-white/90 mb-6">
              Contact us today for fast, reliable, and professional legal services. 
              We're here to handle your process serving and legal support needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button href="tel:8064011749">
                Call (806) 401-1749
              </Button>
              <Button to="/contact" variant="outline">
                Request Quote
              </Button>
              <Button to="/areas-pricing" variant="outline">
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

// Wave separator component
const Wave = () => (
  <svg className="wave-sep" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true">
    <path fill="#1a1a1a" d="M0,64L60,80C120,96,240,128,360,122.7C480,117,600,75,720,69.3C840,64,960,96,1080,117.3C1200,139,1320,149,1380,154.7L1440,160L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z" />
  </svg>
);

// Calendly Inline embed
const CalendlyInline = ({ url }) => {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const ensureScript = () => {
      const existing = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existing) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://assets.calendly.com/assets/external/widget.js';
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    };
    const init = () => {
      if (window.Calendly && url) {
        window.Calendly.initInlineWidget({ url, parentElement: ref.current, resize: true });
        setLoaded(true);
      }
    };
    ensureScript().then(init).catch(() => console.error('Calendly failed to load'));
  }, [url]);
  return (
    <div className="rounded-lg overflow-hidden border reveal" ref={ref} style={{ minHeight: 600 }}>
      {!loaded && (
        <div className="calendly-loading flex items-center justify-center h-[600px] text-sm text-white/60">Loading scheduler…</div>
      )}
    </div>
  );
};

// Holiday Notary Card Component with Smart Dropdown and Real Dates
const HolidayNotaryCard = ({ item, index }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Helper functions to calculate specific holiday dates (defined first)
  const getThirdMonday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstMonday = new Date(firstDay);
    firstMonday.setDate(1 + (8 - firstDay.getDay()) % 7);
    firstMonday.setDate(firstMonday.getDate() + 14); // Third Monday
    return firstMonday;
  };

  const getLastMonday = (year, month) => {
    const lastDay = new Date(year, month + 1, 0);
    const lastMonday = new Date(lastDay);
    lastMonday.setDate(lastDay.getDate() - (lastDay.getDay() + 6) % 7);
    return lastMonday;
  };

  const getFirstMonday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstMonday = new Date(firstDay);
    firstMonday.setDate(1 + (8 - firstDay.getDay()) % 7);
    return firstMonday;
  };

  const getSecondMonday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstMonday = new Date(firstDay);
    firstMonday.setDate(1 + (8 - firstDay.getDay()) % 7);
    firstMonday.setDate(firstMonday.getDate() + 7); // Second Monday
    return firstMonday;
  };

  const getFourthThursday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstThursday = new Date(firstDay);
    firstThursday.setDate(1 + (11 - firstDay.getDay()) % 7);
    firstThursday.setDate(firstThursday.getDate() + 21); // Fourth Thursday
    return firstThursday;
  };

  const getGoodFriday = (year) => {
    // Simple Easter calculation (Good Friday is 2 days before Easter)
    const easter = getEaster(year);
    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2);
    return goodFriday;
  };

  const getEaster = (year) => {
    // Basic Easter calculation algorithm
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const n = Math.floor((h + l - 7 * m + 114) / 31);
    const p = (h + l - 7 * m + 114) % 31;
    return new Date(year, n - 1, p + 1);
  };

  const getSecondSunday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstSunday = new Date(firstDay);
    firstSunday.setDate(1 + (7 - firstDay.getDay()) % 7);
    firstSunday.setDate(firstSunday.getDate() + 7); // Second Sunday
    return firstSunday;
  };

  const getThirdSunday = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstSunday = new Date(firstDay);
    firstSunday.setDate(1 + (7 - firstDay.getDay()) % 7);
    firstSunday.setDate(firstSunday.getDate() + 14); // Third Sunday
    return firstSunday;
  };

  // Format date as "Month DD, YYYY"
  const formatDate = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Calculate which year to use for holiday dates
  const getHolidayYear = () => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-indexed
    const currentDate = today.getDate();
    
    // If it's December 31st, start showing next year's holidays
    if (currentMonth === 11 && currentDate === 31) {
      return today.getFullYear() + 1;
    }
    return today.getFullYear();
  };

  // Calculate complete holiday list with actual dates for the appropriate year
  const getHolidaysWithActualDates = () => {
    const year = getHolidayYear();
    
    return [
      { 
        name: "New Year's Day", 
        actualDate: new Date(year, 0, 1),
        formattedDate: `January 1, ${year}`
      },
      { 
        name: "Martin Luther King Jr. Day", 
        actualDate: getThirdMonday(year, 0),
        formattedDate: formatDate(getThirdMonday(year, 0))
      },
      { 
        name: "Presidents' Day", 
        actualDate: getThirdMonday(year, 1),
        formattedDate: formatDate(getThirdMonday(year, 1))
      },
      { 
        name: "Good Friday", 
        actualDate: getGoodFriday(year),
        formattedDate: formatDate(getGoodFriday(year))
      },
      { 
        name: "Easter", 
        actualDate: getEaster(year),
        formattedDate: formatDate(getEaster(year))
      },
      { 
        name: "Mother's Day", 
        actualDate: getSecondSunday(year, 4),
        formattedDate: formatDate(getSecondSunday(year, 4))
      },
      { 
        name: "Memorial Day", 
        actualDate: getLastMonday(year, 4),
        formattedDate: formatDate(getLastMonday(year, 4))
      },
      { 
        name: "Father's Day", 
        actualDate: getThirdSunday(year, 5),
        formattedDate: formatDate(getThirdSunday(year, 5))
      },
      { 
        name: "Juneteenth", 
        actualDate: new Date(year, 5, 19),
        formattedDate: `June 19, ${year}`
      },
      { 
        name: "Independence Day", 
        actualDate: new Date(year, 6, 4),
        formattedDate: `July 4, ${year}`
      },
      { 
        name: "Labor Day", 
        actualDate: getFirstMonday(year, 8),
        formattedDate: formatDate(getFirstMonday(year, 8))
      },
      { 
        name: "Columbus Day", 
        actualDate: getSecondMonday(year, 9),
        formattedDate: formatDate(getSecondMonday(year, 9))
      },
      { 
        name: "Halloween", 
        actualDate: new Date(year, 9, 31),
        formattedDate: `October 31, ${year}`
      },
      { 
        name: "Veterans Day", 
        actualDate: new Date(year, 10, 11),
        formattedDate: `November 11, ${year}`
      },
      { 
        name: "Thanksgiving Day", 
        actualDate: getFourthThursday(year, 10),
        formattedDate: formatDate(getFourthThursday(year, 10))
      },
      { 
        name: "Christmas Eve", 
        actualDate: new Date(year, 11, 24),
        formattedDate: `December 24, ${year}`
      },
      { 
        name: "Christmas Day", 
        actualDate: new Date(year, 11, 25),
        formattedDate: `December 25, ${year}`
      },
      { 
        name: "New Year's Eve", 
        actualDate: new Date(year, 11, 31),
        formattedDate: `December 31, ${year}`
      }
    ];
  };

  const holidays = getHolidaysWithActualDates();

  // Get next upcoming holiday
  const getNextHoliday = () => {
    const today = new Date();
    
    // Find the next holiday that hasn't passed yet
    const upcomingHolidays = holidays.filter(holiday => {
      // Set time to end of day for the holiday to handle same-day scenarios
      const holidayEndOfDay = new Date(holiday.actualDate);
      holidayEndOfDay.setHours(23, 59, 59, 999);
      return holidayEndOfDay >= today;
    });
    
    if (upcomingHolidays.length > 0) {
      return upcomingHolidays[0]; // Return the earliest upcoming holiday
    } else {
      // If no holidays left this year, return next year's New Year's Day
      const nextYear = today.getFullYear() + 1;
      return {
        name: "New Year's Day",
        actualDate: new Date(nextYear, 0, 1),
        formattedDate: `January 1, ${nextYear}`
      };
    }
  };

  // Duplicate functions removed - now defined at top of component

  const nextHoliday = getNextHoliday();

  if (item.holidays) {
    return (
      <div className="rounded-lg border bg-white border-gray-200 p-4 reveal shadow-lg" style={{ transitionDelay: `${0.1 + index * 0.05}s` }}>
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-700">{item.label}</span>
          <span className="text-lg font-extrabold text-[hsl(357,85%,52%)]">{item.price}</span>
        </div>
        <p className="mt-1 text-xs text-gray-600">{item.note}</p>
        
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="mb-2">
            <h4 className="text-xs font-semibold text-gray-700">Next Holiday: <span className="text-[hsl(357,85%,52%)]">{nextHoliday.name} - {nextHoliday.formattedDate}</span></h4>
          </div>
          <div className="text-center">
            <button 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="text-xs text-[hsl(357,85%,52%)] hover:text-[hsl(357,85%,47%)] font-semibold flex items-center justify-center mx-auto"
            >
              {isDropdownOpen ? 'Hide' : 'View All'} Holidays
              <svg 
                className={`w-3 h-3 ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {isDropdownOpen && (
            <div className="mt-3 bg-gray-50 rounded-lg p-3 border">
              <div className="grid gap-1">
                {holidays.map((holiday, idx) => {
                  const isNextHoliday = holiday.name === nextHoliday.name;
                  return (
                    <div key={holiday.name} className={`flex justify-between text-xs py-1 ${isNextHoliday ? 'font-semibold' : ''}`}>
                      <span className={isNextHoliday ? 'text-[hsl(357,85%,52%)]' : 'text-gray-600'}>{holiday.name}</span>
                      <span className={isNextHoliday ? 'text-[hsl(357,85%,52%)]' : 'text-gray-500'}>{holiday.formattedDate}</span>
                    </div>
                  );
                })}
              </div>
              {/* Contact text removed per user request */}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular notary card (non-holiday)
  return (
    <div className="rounded-lg border bg-white border-gray-200 p-4 reveal shadow-lg" style={{ transitionDelay: `${0.1 + index * 0.05}s` }}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold uppercase tracking-wide text-gray-700">{item.label}</span>
        <span className="text-lg font-extrabold text-[hsl(357,85%,52%)]">{item.price}</span>
      </div>
      <p className="mt-1 text-xs text-gray-600">{item.note}</p>
    </div>
  );
};

export const Notary = () => {
  return (
    <section className="black-section">
      <Container className="py-16 text-center">
        <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">Schedule Notary</h1>
        <p className="text-white/70 mt-2 text-lg">Mobile Notary Services in Lubbock, TX & Surrounding West Texas Areas</p>
        
        {/* What to Bring Section */}
        <div className="mt-8 mb-12 max-w-4xl mx-auto">
          <div className="gradient-outline p-8 reveal">
            <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6">What You Need to Bring</h2>
            <p className="text-white/90 mb-6 text-lg">
              To complete your notarization, you must bring valid identification. Acceptable forms of ID include:
            </p>
            
            <div className="grid gap-4 md:grid-cols-2 text-left">
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center">
                  <svg className="w-5 h-5 text-[hsl(357,85%,52%)] mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Acceptable Identification
                </h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• State Issued Driver's License</li>
                  <li>• State Issued ID Card</li>
                  <li>• US Passport</li>
                  <li>• Military ID</li>
                </ul>
              </div>
              
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center">
                  <svg className="w-5 h-5 text-[hsl(357,85%,52%)] mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Important Requirements
                </h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• Must be current and not expired</li>
                  <li>• Must have a clear photo</li>
                  <li>• Must be the original document</li>
                  <li>• Photocopies are not acceptable</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-[hsl(357,85%,52%)]/20 border border-[hsl(357,85%,52%)]/40 rounded-lg">
              <p className="text-white font-semibold text-sm">
                <span className="text-[hsl(357,85%,52%)]">Note:</span> Please ensure your ID is current and matches the name on the documents to be notarized.
              </p>
            </div>
          </div>
        </div>
        
        {/* Booking Widget - replaces Calendly */}
        <div className="mt-6 max-w-4xl mx-auto">
          <NotaryBookingWidget />
        </div>
        
        <div className="my-12 max-w-7xl mx-auto"><Divider /></div>
        <h2 className="text-2xl font-bold">Information / Price List</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2 max-w-4xl mx-auto text-left md:text-center">
          {[
            { section: "Weekday Notary", items: [ { label: "Routine Notary", price: "$75.00", note: "Must be scheduled 4 hours in advance." }, { label: "Rush Notary", price: "$150.00", note: "Must be scheduled 1 hour in advance." } ] },
            { section: "Weekend Notary", items: [ { label: "Routine Notary", price: "$115.00", note: "Must be scheduled 4 hours in advance." }, { label: "Rush Notary", price: "$230.00", note: "Must be scheduled 1 hour in advance." } ] },
            { section: "Mortgage/Loan Notary", items: [ { label: "Routine Notary", price: "$175.00", note: "Must be scheduled 4 hours in advance." }, { label: "Rush Notary", price: "$350.00", note: "Must be scheduled 1 hour in advance." } ] },
            { section: "Holiday Notary", items: [ { label: "Notary", price: "$225.00", note: "Available on major holidays throughout the year.", holidays: true } ] },
          ].map((block, idx) => (
            <div key={block.section} className="gradient-outline p-6 reveal" style={{ transitionDelay: `${0.06 + idx * 0.08}s` }}>
              <h3 className="text-xl font-semibold">{block.section}</h3>
              <div className="mt-3 grid gap-3">
                {block.items.map((it, i) => (
                  <HolidayNotaryCard key={it.label} item={it} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* Service Area Section for SEO */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="gradient-outline p-6 reveal">
            <h2 className="text-xl font-bold text-[hsl(357,85%,52%)] mb-4">Mobile Notary Service Areas</h2>
            <p className="text-white/80 text-sm leading-relaxed">
              We provide mobile notary services throughout West Texas including <strong>Lubbock</strong>, Wolfforth, Shallowater, Slaton, Idalou, 
              Levelland, Littlefield, Plainview, Brownfield, Post, Tahoka, Lamesa, and surrounding communities. 
              Whether you need a notary for loan signings, affidavits, power of attorney, real estate documents, or other legal documents, 
              we come to you. Available 7 days a week with same-day appointments.
            </p>
            <p className="text-white/60 text-xs mt-3">
              Serving Lubbock County, Hockley County, Hale County, Terry County, Lynn County, and more.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
};

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Process Serving Basics",
      questions: [
        {
          question: "What is process serving?",
          answer: "Process serving is the legal procedure of delivering court documents to parties involved in a legal case. This includes summons, complaints, subpoenas, and other legal notices. Professional process servers ensure documents are delivered according to legal requirements and provide proof of service to the courts."
        },
        {
          question: "Why can't I serve documents myself?",
          answer: "Texas law requires process servers to be certified by the Judicial Branch Certification Commission (JBCC). Additionally, using a neutral third party ensures proper legal procedures are followed and eliminates any potential conflicts of interest that could invalidate the service."
        },
        {
          question: "How long does it take to serve documents?",
          answer: "Service times vary by zone and service type. Zone 1 (Lubbock and Childress counties) typically receives same-day service. Zones 2-5 range from next-day to 5-day service windows. Rush service is available for urgent needs with expedited delivery."
        },
        {
          question: "What happens if someone refuses to accept the documents?",
          answer: "In Texas, individuals cannot legally 'refuse' service. If someone won't take the documents, we can complete service by placing the documents near the person and explaining what they are. The service is still legally valid even if they refuse to physically accept the papers."
        },
        {
          question: "Can documents be served on weekends or holidays?",
          answer: "According to Texas Rule of Civil Procedure 6, civil process cannot be served on Sundays except in emergency situations like injunctions. Saturday service is generally permitted. We offer special weekend and holiday notary services for urgent needs."
        }
      ]
    },
    {
      category: "Service Areas & Pricing",
      questions: [
        {
          question: "Which areas do you serve?",
          answer: "We serve from two strategic bases: Lubbock Base covers 5 zones in West Texas including Lubbock, Hale, Crosby, Lynn, Hockley counties and extended areas. Childress Base covers 4 zones in North Texas Panhandle including Childress, Hall, Collingsworth, Cottle, Hardeman counties. We also provide complete New Mexico statewide coverage at $125 routine / $175 rush."
        },
        {
          question: "How is pricing determined?",
          answer: "Pricing is zone-based starting at $75 for Zone 1 routine service. Each zone has specific pricing for routine and rush service: Zone 1 ($75/$100), Zone 2 ($100/$130), Zone 3 ($125/$160), Zone 4 ($150/$195), Zone 5 ($175/$225), Zone 6 ($200/$255). All prices are per address served."
        },
        {
          question: "Do you serve outside Texas and New Mexico?",
          answer: "Yes, through our network of certified process servers nationwide, we can serve documents in all 50 states. We also have experience with Hague Convention services for international process serving requirements. Contact us for custom quotes."
        },
        {
          question: "What if the person has moved or can't be located?",
          answer: "We use advanced skip tracing techniques and comprehensive databases to locate individuals. Our skip tracing service ($35 per entity) searches current and past addresses, phone numbers, employers, relatives, and associates to find the current location."
        }
      ]
    },
    {
      category: "Legal Requirements & Methods",
      questions: [
        {
          question: "What methods of service are allowed in Texas?",
          answer: "Texas law permits several methods: personal hand delivery, substitute service at a person's home or workplace, service through certified mail with tracking, and in some cases, service by publication with court approval. Each method must follow specific legal guidelines."
        },
        {
          question: "Can someone else at the address accept service?",
          answer: "Yes, under Texas rules, documents can be served to another competent adult at the recipient's residence or place of business if direct personal service is not possible. This is called 'substitute service' and is legally valid when properly executed."
        },
        {
          question: "How is proof of service provided?",
          answer: "We provide a court-acceptable Affidavit of Service that details when, where, and how documents were served. This notarized document includes all information required by the courts and serves as legal proof that proper service was completed."
        },
        {
          question: "What happens if service cannot be completed?",
          answer: "If we cannot complete service after diligent attempts, we provide an Affidavit of Non-Service or Due Diligence affidavit detailing all attempts made. This may allow the court to approve alternative service methods like service by publication."
        }
      ]
    },
    {
      category: "Case Management & Tracking",
      questions: [
        {
          question: "How can I track my case status?",
          answer: "We use a comprehensive case management system that provides real-time tracking. All updates are automatically sent to your email, including attempt details, successful service confirmation, and any special circumstances encountered during service."
        },
        {
          question: "How many attempts are made to serve documents?",
          answer: "We make up to 4 attempts to serve documents for both routine and rush service. Each attempt is documented with detailed information about the time, location, circumstances, and any individuals contacted during the attempt."
        },
        {
          question: "Do I need to check on my case status?",
          answer: "No, our automated system sends updates directly to your email for every attempt and successful service. You'll receive comprehensive information without needing to contact us for status updates, saving you time and ensuring transparency."
        },
        {
          question: "What if I need to modify service instructions?",
          answer: "Contact us immediately if you need to change service instructions, add special handling requirements, or update address information. Our case management system allows for real-time updates to ensure accurate service."
        }
      ]
    },
    {
      category: "Additional Services & Professional Standards",
      questions: [
        {
          question: "Do you offer notary services?",
          answer: "Yes, we provide mobile notary services including routine weekday service, weekend service, holiday service, and specialized mortgage/loan document notarization. Acceptable ID includes State Issued Driver's License, State Issued ID Card, US Passport, and Military ID."
        },
        {
          question: "What is skip tracing and when is it needed?",
          answer: "Skip tracing is locating individuals who cannot be found at known addresses. We use multiple databases to find current addresses, phone numbers, employers, relatives, and associates. This service costs $35 per entity and is often needed when initial service attempts are unsuccessful."
        },
        {
          question: "Are you licensed and insured?",
          answer: "Yes, WesTex Legal is fully licensed by the Texas Judicial Branch Certification Commission (JBCC), bonded, and insured. We are proud members of the National Association of Professional Process Servers (NAPPS), maintaining the highest professional standards."
        },
        {
          question: "Do you provide background checks?",
          answer: "Yes, we offer comprehensive background checks including criminal records, evictions, foreclosures, bankruptcies, liens, judgments, property records, corporate filings, and professional licenses. Pricing is $65 per entity with detailed reporting."
        },
        {
          question: "What makes WesTex Legal different from other process servers?",
          answer: "We combine 15+ years of legal industry experience with advanced technology, offer real-time case tracking, maintain strategic locations for comprehensive Texas/New Mexico coverage, provide 24/7 availability, and focus on client satisfaction with professional, reliable service delivery."
        }
      ]
    },
    {
      category: "Client Portal",
      questions: [
        {
          question: "What is the Client Portal?",
          answer: "The Client Portal is a secure online platform where you can view all your service jobs, track their status in real-time, download affidavits and service documents, and manage your account. It provides 24/7 access to your case information without needing to call or email us."
        },
        {
          question: "Do I have to use the portal to submit jobs?",
          answer: "No, the portal is completely optional for job submission. You can continue sending jobs via email exactly as you're used to doing. Once we set up your job in our case management system, it will automatically appear in your portal for tracking. The portal is primarily designed to give you real-time visibility into your jobs, not to replace your current workflow."
        },
        {
          question: "How do I access the Client Portal?",
          answer: "Visit westexlegal.com/portal to log in or register. If you're an existing client, contact us to receive your portal invitation. New clients can register directly, and once your account is approved, you'll have immediate access to all your jobs."
        },
        {
          question: "What can I do in the Client Portal?",
          answer: "In the portal, you can: view all your active and completed jobs, see detailed attempt history and timeline for each service (including the specific address attempted), download Affidavits of Service and other documents, and track job status in real-time. You can also submit new jobs through the portal if you prefer, but this is entirely optional."
        },
        {
          question: "How do I download my Affidavit of Service?",
          answer: "Once a job is completed, log into the Client Portal, navigate to the job details, and click the download button in the Affidavits section. You'll receive the official affidavit document instantly, available anytime you need it."
        },
        {
          question: "Will my emailed jobs appear in the portal?",
          answer: "Yes! When you send us a job via email and we enter it into our case management system, it automatically syncs to your Client Portal. You'll be able to track its progress, view all service attempts, and download completed affidavits - all without changing how you submit jobs."
        },
        {
          question: "Is the Client Portal secure?",
          answer: "Yes, the Client Portal uses industry-standard encryption and secure authentication to protect your information. All data is transmitted securely, and your account is protected with password authentication."
        },
        {
          question: "Is there a referral program?",
          answer: "Yes! Once registered, you'll receive a unique referral code. Share it with colleagues or other attorneys, and when someone uses your code to sign up and submits their first job, you'll earn a $25 credit toward your next invoice. You can find your referral code and track your earnings in the Client Portal dashboard."
        }
      ]
    }
  ];

  const toggleAccordion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="black-section">
      <Container className="py-16">
        <div className="text-center mb-12">
          <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">Frequently Asked Questions</h1>
          <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
          <p className="text-lg text-white/90 max-w-3xl mx-auto">
            Find answers to common questions about our process serving, notary services, 
            and legal support throughout Texas and New Mexico.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-12">
          {faqs.map((category, categoryIndex) => (
            <div key={category.category} className="reveal" style={{ transitionDelay: `${0.1 + categoryIndex * 0.1}s` }}>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[hsl(357,85%,52%)] to-[hsl(357,85%,42%)] rounded-2xl mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    {categoryIndex === 0 && (
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    )}
                    {categoryIndex === 1 && (
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    )}
                    {categoryIndex === 2 && (
                      <>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </>
                    )}
                    {categoryIndex === 3 && (
                      <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>
                    )}
                    {categoryIndex === 4 && (
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    )}
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">{category.category}</h2>
                <div className="h-1 w-24 bg-gradient-to-r from-[hsl(357,85%,52%)] to-[hsl(357,85%,42%)] mx-auto rounded-full"></div>
              </div>

              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const index = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openIndex === index;
                  
                  return (
                    <div key={`faq-${categoryIndex}-${questionIndex}`} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleAccordion(categoryIndex, questionIndex)}
                        className="w-full px-6 py-5 text-left flex items-center justify-between group hover:bg-white/5 transition-all duration-200"
                      >
                        <h3 className="font-semibold text-white pr-4 group-hover:text-[hsl(357,85%,52%)] transition-colors text-lg">{faq.question}</h3>
                        <div className={`flex-shrink-0 transform transition-all duration-200 ${isOpen ? 'rotate-180 text-[hsl(357,85%,52%)]' : 'text-white/60'}`}>
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                          </svg>
                        </div>
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-5 border-t border-white/10 bg-black/20 animate-in slide-in-from-top-2 duration-200">
                          <p className="text-white/90 leading-relaxed pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8 max-w-2xl mx-auto reveal" style={{ transitionDelay: '.6s' }}>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[hsl(357,85%,52%)] to-[hsl(357,85%,42%)] rounded-2xl mb-6">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Still Have Questions?</h3>
            <p className="text-white/90 mb-6 leading-relaxed">
              Can't find the answer you're looking for? Our experienced team is ready to help 
              with any questions about our services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button href="tel:8064011749">
                Call (806) 401-1749
              </Button>
              <Button to="/contact" variant="outline">
                Send Message
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 text-center">
          <h4 className="font-semibold text-white mb-4">Need More Information?</h4>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Button to="/services" variant="outline" className="text-xs">
              View Services
            </Button>
            <Button to="/areas-covered" variant="outline" className="text-xs">
              Coverage Areas
            </Button>
            <Button to="/pricing" variant="outline" className="text-xs">
              Pricing Information
            </Button>
            <Button to="/notary" variant="outline" className="text-xs">
              Notary Services
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    case_type: 'General Inquiry'
  });
  const [status, setStatus] = useState({ type: 'idle', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'loading', msg: 'Sending your message...' });

    try {
      // Submit directly to Formspree
      const response = await axios.post('https://formspree.io/f/xgvlevdk', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        case_type: formData.case_type,
        _replyto: formData.email, // Formspree will use this for reply-to
        _subject: `New Contact Form Submission: ${formData.subject}` // Custom email subject
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.status === 200) {
        setStatus({ 
          type: 'success', 
          msg: 'Thank you for your message! We will get back to you within 24 hours.' 
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          case_type: 'General Inquiry'
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ 
        type: 'error', 
        msg: 'There was an error sending your message. Please try again or call us directly at (806) 401-1749.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="black-section">
      <Container className="py-16">
        <div className="text-center mb-12">
          <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">Contact Us</h1>
          <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Ready to get started? Contact us today for professional process serving, 
            notary services, and legal support throughout Texas and New Mexico.
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2 max-w-6xl mx-auto">
          
          {/* Contact Form */}
          <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.1s' }}>
            <h2 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                    placeholder=""
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                    placeholder=""
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                    placeholder=""
                  />
                </div>
                
                <div>
                  <label htmlFor="case_type" className="block text-sm font-semibold text-white mb-2">
                    Service Type
                  </label>
                  <select
                    id="case_type"
                    name="case_type"
                    value={formData.case_type}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Process Serving">Process Serving</option>
                    <option value="Rush Service">Rush Service</option>
                    <option value="Background Check">Background Check</option>
                    <option value="Document Retrieval">Document Retrieval</option>
                    <option value="Skip Tracing">Skip Tracing</option>
                    <option value="Vehicle Search">Vehicle Search</option>
                    <option value="Mobile Notary">Mobile Notary</option>
                    <option value="Statewide Service">Statewide Service</option>
                    <option value="Nationwide Service">Nationwide Service</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-white mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 bg-white"
                  placeholder=""
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(357,85%,52%)] focus:border-[hsl(357,85%,52%)] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed resize-vertical text-gray-900 bg-white"
                  placeholder="Please provide details about your legal service needs, including any deadlines, locations, and specific requirements..."
                ></textarea>
              </div>

              <div className="flex items-center gap-4">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
                
                {status.type !== 'idle' && (
                  <div className={`flex-1 text-sm font-medium ${
                    status.type === 'success' ? 'text-green-600' : 
                    status.type === 'error' ? 'text-red-600' : 'text-white/80'
                  }`}>
                    {status.msg}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            
            {/* Primary Contact */}
            <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.2s' }}>
              <h3 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-4 flex items-center">
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Get in Touch
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[hsl(357,85%,52%)] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <a href="tel:8064011749" className="text-[hsl(357,85%,52%)] hover:underline">
                      (806) 401-1749
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[hsl(357,85%,52%)] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <div>
                    <p className="font-semibold">Email</p>
                    <a href="mailto:WesTexLegal@gmail.com" className="text-[hsl(357,85%,52%)] hover:underline">
                      WesTexLegal@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[hsl(357,85%,52%)] mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-sm text-white/80">
                      Texas (West & Panhandle)<br/>
                      New Mexico (Statewide)<br/>
                      Nationwide Network Available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="gradient-outline p-8 reveal" style={{ transitionDelay: '.3s' }}>
              <h3 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-4 flex items-center">
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                </svg>
                Business Hours
              </h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Process Serving:</span>
                  <span>24/7 Available</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Notary Services:</span>
                  <span>10:00 AM - 8:00 PM</span>
                </div>
                <div className="mt-4 p-3 bg-[hsl(357,85%,52%)]/10 border border-[hsl(357,85%,52%)]/20 rounded-lg">
                  <p className="text-xs text-[hsl(357,85%,52%)]">
                    <strong>24/7 Service Available:</strong> Emergency and rush orders 
                    can be accommodated any time with appropriate fees.
                  </p>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="max-w-2xl mx-auto">
              <div className="gradient-outline p-8 text-center reveal" style={{ transitionDelay: '.4s' }}>
                <h3 className="text-xl font-bold text-[hsl(357,85%,52%)] mb-3">Need Immediate Service?</h3>
                <p className="text-sm text-white/80 mb-4">
                  For urgent process serving needs, call us directly for faster response times.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button href="tel:8064011749">
                    Call Now: (806) 401-1749
                  </Button>
                  <Button to="/pricing" variant="outline">
                    View Pricing
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

const Footer = () => {
  const handleLinkClick = () => {
    // Scroll to top when footer links are clicked
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="mt-16 border-t bg-black border-white/20">
      <Container className="py-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <img src={NAPPS_BADGE} alt="NAPPS membership badge" className="h-10 w-auto" />
          <p className="text-sm text-white/70">© 2016–{new Date().getFullYear()} WesTex Legal — All Rights Reserved.</p>
        </div>
        <nav className="flex flex-wrap gap-4 text-sm">
          <Link to="/" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Home</Link>
          <Link to="/about" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">About Us</Link>
          <Link to="/services" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Services</Link>
          <Link to="/areas-pricing" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Areas & Pricing</Link>
          <Link to="/notary" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Notary</Link>
          <Link to="/faq" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">FAQ</Link>
          <Link to="/contact" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Contact Us</Link>
          <Link to="/portal/login" onClick={handleLinkClick} className="hover:underline text-white/80 hover:text-white">Client Portal</Link>
        </nav>
      </Container>
    </footer>
  );
};

const Home = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Prevent hydration mismatch by ensuring consistent rendering
  if (!isClient) {
    return (
      <div className="min-h-screen">
        <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
        <div className="flex justify-center items-center py-20">
          <div className="text-white/80">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <HowItWorks />
      <Affiliations />
      <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
      <GoogleReviews />
    </>
  );
};

// Enhanced Service Areas & Pricing with Interactive Tabs and Zone-based Structure
export const AreasCovered = () => {
  const [activeTab, setActiveTab] = useState('zones');
  const [selectedZone, setSelectedZone] = useState(null);

  // Comprehensive database of incorporated cities/municipalities only for search
  const allLubbockTowns = {
    zone1: ["Lubbock", "Wolfforth", "Shallowater", "Idalou", "Slaton", "Smyer", "Buffalo Springs Lake", "New Deal", "Ransom Canyon"],
    zone2: ["Abernathy", "Ropesville", "Lorenzo", "Anton", "Levelland", "Ralls", "Petersburg", "Tahoka", "New Home", "Meadow", "Southland"],
    zone3: ["Littlefield", "Crosbyton", "Hale Center", "Brownfield", "Post", "Plainview", "Sudan", "Olton", "Whiteface", "Whitharral", "Spade", "Fieldton", "Wellman", "O'Donnell", "Sundown", "Amherst"],
    zone4: ["Morton", "Lockney", "Seagraves", "Lamesa", "Kress", "Muleshoe", "Plains", "Justiceburg", "Dickens", "Spur", "Edmonson", "Welch", "Loop", "Floydada", "Earth", "Hart", "Springlake"],
    zone5: ["Dimmitt", "Denver City", "Seminole", "Snyder", "Nazareth", "Tulia"],
    zone6: ["Farwell", "Bovina", "Friona", "Andrews"]
  };

  const allChildressTowns = {
    zone1: ["Childress", "Estelline"],
    zone2: ["Memphis", "Quanah", "Wellington", "Paducah", "Newlin", "Dodson"],
    zone3: ["Crowell", "Turkey", "Chillicothe", "Clarendon", "Shamrock", "Vernon", "Quitaque", "Lakeview"],
    zone4: ["Roaring Springs", "Silverton", "Benjamin", "Guthrie", "Matador", "Wheeler"],
    zone5: ["Goree", "Munday", "Briscoe"]
  };

  // New Mexico cities database - major cities across all 33 counties
  const newMexicoCities = [
    // Major Cities
    "Albuquerque", "Las Cruces", "Rio Rancho", "Santa Fe", "Roswell", "Farmington", "Clovis", "Hobbs",
    "Alamogordo", "Carlsbad", "Gallup", "Deming", "Las Vegas", "Chaparral", "Sunland Park", "Los Alamos",
    // County Seats & Regional Centers
    "Silver City", "Portales", "Ruidoso", "Artesia", "Bloomfield", "Grants", "Lovington", "Socorro",
    "Truth or Consequences", "Tucumcari", "Belen", "Española", "Anthony", "Bernalillo", "Corrales",
    // Additional Municipalities
    "Aztec", "Bayard", "Bosque Farms", "Cloudcroft", "Cuba", "Edgewood", "Eunice", "Jal", "Lordsburg",
    "Milan", "Moriarty", "Raton", "Red River", "Reserve", "Santa Rosa", "Springer", "Taos", "Tatum",
    "Texico", "Tijeras", "Tularosa", "Wagon Mound", "Clayton", "Carrizozo", "Capitan", "Corona",
    "Encino", "Estancia", "Mountainair", "Willard", "Angel Fire", "Cimarron", "Eagle Nest", "Maxwell",
    "Questa", "Cerrillos", "Madrid", "Stanley", "Pecos", "San Jose", "Villanueva", "Watrous"
  ];

  // Out of Area Cities Database - 100,000+ population cities (excluding current coverage areas)
  const outOfAreaCities = [
    // ALABAMA
    "Birmingham", "Huntsville", "Mobile", "Montgomery", "Tuscaloosa",
    // ALASKA
    "Anchorage",
    // ARIZONA
    "Chandler", "Gilbert", "Glendale", "Mesa", "Peoria", "Phoenix", "Scottsdale", "Surprise", "Tempe", "Tucson", "Yuma",
    // ARKANSAS
    "Fayetteville", "Little Rock",
    // CALIFORNIA
    "Anaheim", "Antioch", "Bakersfield", "Berkeley", "Burbank", "Carlsbad", "Chula Vista", "Concord", "Corona", "Costa Mesa", "Daly City", "Downey", "Elk Grove", "Escondido", "Fairfield", "Fontana", "Fremont", "Fresno", "Fullerton", "Garden Grove", "Glendale", "Hayward", "Huntington Beach", "Inglewood", "Irvine", "Lancaster", "Long Beach", "Los Angeles", "Modesto", "Moreno Valley", "Murrieta", "Norwalk", "Oakland", "Oceanside", "Ontario", "Orange", "Oxnard", "Palmdale", "Pasadena", "Pomona", "Rancho Cucamonga", "Rialto", "Richmond", "Riverside", "Roseville", "Sacramento", "Salinas", "San Bernardino", "San Diego", "San Francisco", "San Jose", "San Mateo", "Santa Ana", "Santa Clara", "Santa Clarita", "Santa Maria", "Santa Rosa", "Simi Valley", "Stockton", "Sunnyvale", "Temecula", "Thousand Oaks", "Torrance", "Tracy", "Vallejo", "Ventura", "Victorville", "Visalia", "West Covina",
    // COLORADO
    "Arvada", "Aurora", "Boulder", "Centennial", "Colorado Springs", "Denver", "Fort Collins", "Greeley", "Lakewood", "Pueblo", "Thornton", "Westminster",
    // CONNECTICUT
    "Bridgeport", "Hartford", "New Haven", "Stamford", "Waterbury",
    // DISTRICT OF COLUMBIA
    "Washington",
    // FLORIDA
    "Cape Coral", "Clearwater", "Coral Springs", "Deltona", "Fort Lauderdale", "Gainesville", "Hialeah", "Hollywood", "Jacksonville", "Lakeland", "Miami", "Miami Gardens", "Miramar", "Orlando", "Palm Bay", "Palm Coast", "Pembroke Pines", "Plantation", "Port St. Lucie", "St. Petersburg", "Sunrise", "Tallahassee", "Tampa", "West Palm Beach",
    // GEORGIA
    "Atlanta", "Augusta", "Columbus", "Macon", "Roswell", "Sandy Springs", "Savannah", "South Fulton",
    // HAWAII
    "Honolulu",
    // IDAHO
    "Boise", "Meridian", "Nampa",
    // ILLINOIS
    "Aurora", "Chicago", "Elgin", "Joliet", "Naperville", "Peoria", "Rockford", "Springfield",
    // INDIANA
    "Carmel", "Evansville", "Fort Wayne", "Indianapolis", "South Bend",
    // IOWA
    "Cedar Rapids", "Davenport", "Des Moines",
    // KANSAS
    "Kansas City", "Olathe", "Overland Park", "Topeka", "Wichita",
    // KENTUCKY
    "Lexington", "Louisville",
    // LOUISIANA
    "Baton Rouge", "Lafayette", "New Orleans", "Shreveport",
    // MARYLAND
    "Baltimore",
    // MASSACHUSETTS
    "Boston", "Cambridge", "Lowell", "Springfield", "Worcester",
    // MICHIGAN
    "Ann Arbor", "Detroit", "Grand Rapids", "Lansing", "Sterling Heights", "Warren",
    // MINNESOTA
    "Minneapolis", "Rochester", "St. Paul",
    // MISSISSIPPI
    "Jackson",
    // MISSOURI
    "Columbia", "Independence", "Kansas City", "Springfield", "St. Louis",
    // MONTANA
    "Billings",
    // NEBRASKA
    "Lincoln", "Omaha",
    // NEVADA
    "Henderson", "Las Vegas", "North Las Vegas", "Reno",
    // NEW HAMPSHIRE
    "Manchester",
    // NEW JERSEY
    "Camden", "Elizabeth", "Jersey City", "Newark", "Paterson",
    // NEW YORK
    "Albany", "Buffalo", "New York", "Rochester", "Syracuse", "Yonkers",
    // NORTH CAROLINA
    "Cary", "Charlotte", "Durham", "Fayetteville", "Greensboro", "High Point", "Raleigh", "Winston-Salem",
    // NORTH DAKOTA
    "Fargo",
    // OHIO
    "Akron", "Cincinnati", "Cleveland", "Columbus", "Dayton", "Toledo",
    // OKLAHOMA
    "Broken Arrow", "Norman", "Oklahoma City", "Tulsa",
    // OREGON
    "Eugene", "Gresham", "Hillsboro", "Portland", "Salem",
    // PENNSYLVANIA
    "Allentown", "Erie", "Philadelphia", "Pittsburgh",
    // RHODE ISLAND
    "Providence",
    // SOUTH CAROLINA
    "Charleston", "Columbia", "North Charleston",
    // SOUTH DAKOTA
    "Sioux Falls",
    // TENNESSEE
    "Chattanooga", "Clarksville", "Knoxville", "Memphis", "Murfreesboro", "Nashville",
    // TEXAS (excluding Lubbock - already covered)
    "Abilene", "Allen", "Amarillo", "Arlington", "Austin", "Brownsville", "Carrollton", "College Station", "Corpus Christi", "Dallas", "Denton", "Edinburg", "El Paso", "Fort Worth", "Frisco", "Garland", "Georgetown", "Grand Prairie", "Houston", "Irving", "Killeen", "Laredo", "League City", "Lewisville", "McAllen", "McKinney", "Mesquite", "Midland", "Odessa", "Pasadena", "Pearland", "Plano", "Richardson", "Round Rock", "San Angelo", "San Antonio", "Sugar Land", "Waco",
    // UTAH
    "Provo", "Salt Lake City", "West Jordan", "West Valley City",
    // VIRGINIA
    "Alexandria", "Chesapeake", "Hampton", "Newport News", "Norfolk", "Portsmouth", "Richmond", "Virginia Beach",
    // WASHINGTON
    "Bellevue", "Everett", "Federal Way", "Kent", "Renton", "Seattle", "Spokane", "Tacoma", "Vancouver",
    // WISCONSIN
    "Green Bay", "Madison", "Milwaukee"
  ];

  // Helper function to get all cities for a zone
  const getAllCitiesForZone = (hub, zoneNumber) => {
    if (hub === 'Lubbock') {
      return allLubbockTowns[`zone${zoneNumber}`] || [];
    } else if (hub === 'Childress') {
      return allChildressTowns[`zone${zoneNumber}`] || [];
    } else if (hub === 'New Mexico') {
      return newMexicoCities;
    }
    return [];
  };

  // Updated Lubbock zones - Top 6 most populated cities per zone
  const lubbockZones = [
    {
      zone: 1,
      cities: ["Lubbock", "Wolfforth", "Slaton", "Shallowater", "Idalou", "Ransom Canyon"],
      routine: "$75",
      rush: "$100",
      color: "bg-red-600"
    },
    {
      zone: 2, 
      cities: ["Levelland", "Abernathy", "Tahoka", "Ralls", "Petersburg", "Anton"],
      routine: "$100",
      rush: "$130",
      color: "bg-red-500"
    },
    {
      zone: 3,
      cities: ["Plainview", "Brownfield", "Littlefield", "Post", "Crosbyton", "Olton"],
      routine: "$125", 
      rush: "$160",
      color: "bg-red-400"
    },
    {
      zone: 4,
      cities: ["Lamesa", "Muleshoe", "Seagraves", "Lockney", "Kress", "Morton"],
      routine: "$150",
      rush: "$195", 
      color: "bg-red-300"
    },
    {
      zone: 5,
      cities: ["Snyder", "Seminole", "Denver City", "Dimmitt", "Tulia", "Nazareth"],
      routine: "$175",
      rush: "$225",
      color: "bg-red-200"
    },
    {
      zone: 6,
      cities: ["Andrews", "Friona", "Bovina", "Farwell"],
      routine: "$200",
      rush: "$255",
      color: "bg-red-100"
    }
  ];

  // Updated Childress zones - Top 3 most populated cities per zone
  const childressZones = [
    {
      zone: 1,
      cities: ["Childress", "Estelline"],
      routine: "$95",
      rush: "$125",
      color: "bg-red-600"
    },
    {
      zone: 2,
      cities: ["Quanah", "Wellington", "Memphis"],
      routine: "$125",
      rush: "$155",
      color: "bg-red-500"
    },
    {
      zone: 3,
      cities: ["Vernon", "Clarendon", "Shamrock"],
      routine: "$155",
      rush: "$185",
      color: "bg-red-400"
    },
    {
      zone: 4,
      cities: ["Silverton", "Benjamin", "Guthrie"],
      routine: "$185",
      rush: "$215",
      color: "bg-red-300"
    },
    {
      zone: 5,
      cities: ["Munday", "Goree", "Briscoe"],
      routine: "$215",
      rush: "$245",
      color: "bg-red-200"
    }
  ];

  // Coverage Calculator component
  const CoverageCalculator = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);

    const searchCoverage = (term) => {
      if (!term.trim()) {
        setResults([]);
        return;
      }

      const searchResults = [];
      
      // Search Lubbock zones
      lubbockZones.forEach(zone => {
        const allCitiesInZone = getAllCitiesForZone('Lubbock', zone.zone);
        const matchedCities = allCitiesInZone.filter(city => 
          city.toLowerCase().includes(term.toLowerCase())
        );
        
        if (matchedCities.length > 0) {
          searchResults.push({
            hub: 'Lubbock',
            zone: zone.zone,
            routine: zone.routine,
            rush: zone.rush,
            matched: matchedCities,
            color: zone.color,
            type: 'local'
          });
        }
      });

      // Search Childress zones
      childressZones.forEach(zone => {
        const allCitiesInZone = getAllCitiesForZone('Childress', zone.zone);
        const matchedCities = allCitiesInZone.filter(city => 
          city.toLowerCase().includes(term.toLowerCase())
        );
        
        if (matchedCities.length > 0) {
          searchResults.push({
            hub: 'Childress',
            zone: zone.zone,
            routine: zone.routine,
            rush: zone.rush,
            matched: matchedCities,
            color: zone.color,
            type: 'local'
          });
        }
      });

      // Search New Mexico cities
      const newMexicoMatches = newMexicoCities.filter(city => 
        city.toLowerCase().includes(term.toLowerCase())
      );
      
      if (newMexicoMatches.length > 0) {
        searchResults.push({
          hub: 'New Mexico',
          zone: 'Statewide',
          routine: '$125',
          rush: '$175',
          matched: newMexicoMatches,
          color: 'bg-blue-200',
          type: 'local'
        });
      }

      // Search Out of Area cities
      const outOfAreaMatches = outOfAreaCities.filter(city => 
        city.toLowerCase().includes(term.toLowerCase())
      );
      
      if (outOfAreaMatches.length > 0) {
        searchResults.push({
          hub: 'Out of Area',
          zone: 'Network Service',
          routine: '$90 - $175',
          rush: 'Location Dependent',
          matched: outOfAreaMatches,
          color: 'bg-orange-200',
          type: 'out_of_area',
          timeline: '3-5 business days'
        });
      }

      setResults(searchResults);
    };

    const handleCityClick = (cityName) => {
      setSearchTerm(cityName);
      searchCoverage(cityName);
    };

    return (
      <div className="max-w-6xl mx-auto"> {/* Increased max width for map */}
        <div className="gradient-outline p-8 reveal">
          <h3 className="text-2xl font-bold text-[hsl(357,85%,52%)] mb-6 text-center">Find Your Service Zone</h3>
          <div className="mb-6">
            <input
              type="text"
              placeholder="Enter city or town name..."
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:border-[hsl(357,85%,52%)] focus:outline-none"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchCoverage(e.target.value);
              }}
            />
          </div>
          
          {results.length > 0 && (
            <div className="space-y-4 mb-6">
              {results.map((result, index) => (
                <div key={index} className={`${result.type === 'out_of_area' ? 'bg-orange-900/20 border-orange-500/30' : 'bg-white/10 border-white/20'} rounded-lg p-4 border`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 ${result.color} rounded mr-2`}></div>
                      <span className="font-semibold text-white">
                        {result.type === 'out_of_area' ? (
                          <>Out of Area Service - {result.zone}</>
                        ) : (
                          <>{result.hub} Hub - Zone {result.zone}</>
                        )}
                      </span>
                    </div>
                    {result.type === 'out_of_area' && (
                      <span className="text-xs bg-orange-500/20 text-orange-200 px-2 py-1 rounded border border-orange-500/40">
                        Network Partner
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/80 mb-2">
                    Found: {result.matched.slice(0, 5).join(', ')}{result.matched.length > 5 ? ` +${result.matched.length - 5} more` : ''}
                  </div>
                  {result.type === 'out_of_area' ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/70">Pricing: <span className="text-orange-300 font-semibold">{result.routine}</span></span>
                        <span className="text-white/70">Timeline: <span className="text-orange-300 font-semibold">{result.timeline}</span></span>
                      </div>
                      <div className="text-xs text-orange-200/80 bg-orange-900/30 p-2 rounded border border-orange-500/30">
                        <span className="text-orange-300 font-semibold">Note:</span> Served by certified network partner. Contact for exact quote and timeline.
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Routine: <span className="text-[hsl(357,85%,52%)] font-semibold">{result.routine}</span></span>
                      <span className="text-white/70">Rush: <span className="text-[hsl(357,85%,52%)] font-semibold">{result.rush}</span></span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {searchTerm && results.length === 0 && (
            <div className="text-center py-8 text-white/60 mb-6">
              <p>No coverage found for "{searchTerm}".</p>
              <p className="text-sm mt-2">We serve 340+ major cities nationwide through our network partners.</p>
              <p className="text-sm text-orange-300 mt-1">Contact us at <span className="font-semibold">(806) 401-1749</span> for custom quotes in any area.</p>
            </div>
          )}
        </div>
        
        {/* Visual Coverage Map */}
        <CoverageMap onCityClick={handleCityClick} searchTerm={searchTerm} />
      </div>
    );
  };

  // Visual Coverage Map Component
  const CoverageMap = ({ onCityClick, searchTerm }) => {
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [zones, setZones] = useState([]);

    // City coordinates (you'd normally get these from a geocoding service)
    const cityCoordinates = {
      // Lubbock Hub cities
      'Lubbock': { lat: 33.5779, lng: -101.8552 },
      'Wolfforth': { lat: 33.5048, lng: -102.0090 },
      'Slaton': { lat: 33.4373, lng: -101.6429 },
      'Shallowater': { lat: 33.6909, lng: -101.9988 },
      'Idalou': { lat: 33.6626, lng: -101.7068 },
      'Ransom Canyon': { lat: 33.5333, lng: -101.6797 }, // More precise coordinates
      'Smyer': { lat: 33.5881, lng: -102.1638 },
      'Buffalo Springs Lake': { lat: 33.53584, lng: -101.72245 }, // User-provided precise coordinates
      'New Deal': { lat: 33.7334, lng: -101.8296 },
      'Levelland': { lat: 33.5873, lng: -102.3577 },
      'Abernathy': { lat: 33.8348, lng: -101.8429 },
      'Tahoka': { lat: 33.1668, lng: -101.7929 },
      'Ralls': { lat: 33.6884, lng: -101.3851 },
      'Petersburg': { lat: 33.8720, lng: -101.5968 },
      'Anton': { lat: 33.8098, lng: -102.1621 },
      'Ropesville': { lat: 33.4132, lng: -102.1544 },
      'Lorenzo': { lat: 33.6715, lng: -101.5354 },
      'New Home': { lat: 33.3269, lng: -101.9117 }, // Corrected coordinates
      'Plainview': { lat: 34.1848, lng: -101.7068 },
      'Brownfield': { lat: 33.1815, lng: -102.2690 },
      'Littlefield': { lat: 33.9137, lng: -102.3243 },
      'Post': { lat: 33.1901, lng: -101.3790 },
      'Floydada': { lat: 33.9842, lng: -101.3368 },
      'Olton': { lat: 34.1773, lng: -102.1318 },
      'Crosbyton': { lat: 33.6618, lng: -101.2371 },
      'Hale Center': { lat: 34.0659, lng: -101.8466 },
      'Sudan': { lat: 34.0667, lng: -102.5256 },
      'Whiteface': { lat: 33.6001, lng: -102.6096 },
      'Meadow': { lat: 33.3495, lng: -102.2051 },
      'O\'Donnell': { lat: 32.9651, lng: -101.8306 },
      'Sundown': { lat: 33.4537, lng: -102.4888 }, // New addition
      'Lamesa': { lat: 32.7376, lng: -101.9507 },
      'Muleshoe': { lat: 34.2267, lng: -102.7243 },
      'Seagraves': { lat: 32.9426, lng: -102.5635 },
      'Lockney': { lat: 34.1267, lng: -101.4429 },
      'Morton': { lat: 33.7268, lng: -102.7594 },
      'Plains': { lat: 33.1884, lng: -102.8271 },
      'Justiceburg': { lat: 33.0387, lng: -101.2104 },
      'Dickens': { lat: 33.6201, lng: -100.8364 },
      'Spur': { lat: 33.4809, lng: -100.8573 },
      'Welch': { lat: 32.9376, lng: -102.1248 },
      'Snyder': { lat: 32.7179, lng: -100.9176 },
      'Seminole': { lat: 32.7198, lng: -102.6446 },
      'Denver City': { lat: 32.9648, lng: -102.8307 },
      'Dimmitt': { lat: 34.5498, lng: -102.3093 },
      'Earth': { lat: 34.2315, lng: -102.4118 },
      'Andrews': { lat: 32.3179, lng: -102.5446 },
      'Tulia': { lat: 34.5237, lng: -101.7579 },
      'Friona': { lat: 34.6390, lng: -102.7240 },
      'Bovina': { lat: 34.5120, lng: -102.8885 },
      'Farwell': { lat: 34.3834, lng: -103.0365 },
      
      // Childress Hub cities - Complete list (some cities removed from pinpoints but remain searchable)
      'Childress': { lat: 34.4267, lng: -100.2040 },
      'Estelline': { lat: 34.5495, lng: -100.4329 },
      'Quanah': { lat: 34.2973, lng: -99.7451 },
      'Wellington': { lat: 34.8572, lng: -100.2151 },
      'Memphis': { lat: 34.7273, lng: -100.5379 },
      'Paducah': { lat: 34.0131, lng: -100.3093 },
      'Vernon': { lat: 34.1542, lng: -99.2951 },
      'Clarendon': { lat: 34.9373, lng: -100.8890 },
      'Shamrock': { lat: 35.2179, lng: -100.2465 },
      'Crowell': { lat: 33.9831, lng: -99.7248 },
      'Turkey': { lat: 34.3984, lng: -100.8968 },
      'Chillicothe': { lat: 34.2573, lng: -99.5151 },
      'Matador': { lat: 34.0151, lng: -100.8204 },
      'Wheeler': { lat: 35.4487, lng: -100.2704 },
      'Quitaque': { lat: 34.3623, lng: -101.0468 },
      'Silverton': { lat: 34.4717, lng: -101.3046 },
      'Benjamin': { lat: 33.5834, lng: -99.7948 },
      'Guthrie': { lat: 33.6176, lng: -100.3284 },
      'Briscoe': { lat: 35.6445, lng: -100.1729 }
    };
    
    // Note: These towns remain searchable but have no map pinpoints:
    // Lubbock Hub: Southland, Loop, Kress, Edmonson, Hart, Nazareth, Summerfield, Springlake, Amherst, Fieldton, Wellman, Whitharral, Spade
    // Childress Hub: Dodson, Newlin, Lakeview, Goree, Munday, Roaring Springs

    // Zone center coordinates
    const hubCoordinates = {
      'Lubbock': { lat: 33.5779, lng: -101.8552 },
      'Childress': { lat: 34.4267, lng: -100.2040 }
    };

    // Convert Tailwind red colors to hex values for map styling
    const getZoneColor = (colorClass) => {
      const colorMap = {
        'bg-red-600': '#dc2626', // darkest - closest zones
        'bg-red-500': '#ef4444',
        'bg-red-400': '#f87171',
        'bg-red-300': '#fca5a5',
        'bg-red-200': '#fecaca',
        'bg-red-100': '#fee2e2'  // lightest - farthest zones
      };
      return colorMap[colorClass] || '#dc2626';
    };

    // Initialize map
    useEffect(() => {
      if (!window.google || !mapRef.current) return;

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: 33.8, lng: -101.2 }, // Centered between Lubbock and Childress
        zoom: 7,
        styles: [
          {
            "featureType": "all",
            "elementType": "geometry.fill",
            "stylers": [{"saturation": -100}, {"lightness": -20}]
          },
          {
            "featureType": "all",
            "elementType": "labels.text.fill",
            "stylers": [{"color": "#ffffff"}]
          },
          {
            "featureType": "all",
            "elementType": "labels.text.stroke",
            "stylers": [{"color": "#000000"}, {"lightness": 13}]
          },
          {
            "featureType": "administrative",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#000000"}]
          },
          {
            "featureType": "administrative",
            "elementType": "geometry.stroke",
            "stylers": [{"color": "#144b53"}, {"lightness": 14}, {"weight": 1.4}]
          },
          {
            "featureType": "landscape",
            "elementType": "all",
            "stylers": [{"color": "#08304b"}]
          },
          {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [{"color": "#0c4152"}, {"lightness": 5}]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#000000"}]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry.stroke",
            "stylers": [{"color": "#0b434f"}, {"lightness": 25}]
          },
          {
            "featureType": "road.arterial",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#000000"}]
          },
          {
            "featureType": "road.arterial",
            "elementType": "geometry.stroke",
            "stylers": [{"color": "#0b3d51"}, {"lightness": 16}]
          },
          {
            "featureType": "road.local",
            "elementType": "geometry",
            "stylers": [{"color": "#000000"}]
          },
          {
            "featureType": "transit",
            "elementType": "all",
            "stylers": [{"color": "#146474"}]
          },
          {
            "featureType": "water",
            "elementType": "all",
            "stylers": [{"color": "#021019"}]
          }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });

      // Add custom CSS to style Google Maps info window
      const style = document.createElement('style');
      style.innerHTML = `
        .gm-style .gm-style-iw-c {
          background: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
        }
        .gm-style .gm-style-iw-d {
          overflow: visible !important;
        }
        .gm-style .gm-style-iw-t::after {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
      document.head.appendChild(style);

      setMap(mapInstance);
    }, []);

    // Add zone circles and markers
    useEffect(() => {
      if (!map || !window.google) return;

      // Clear existing markers and zones
      markers.forEach(marker => marker.setMap(null));
      zones.forEach(zone => zone.setMap(null));

      const newMarkers = [];
      const newZones = [];

      // Add Lubbock zone city markers (no circles)
      lubbockZones.forEach((zone, index) => {
        const color = getZoneColor(zone.color);

        // Add city markers for this zone
        const allCitiesInZone = getAllCitiesForZone('Lubbock', zone.zone);
        allCitiesInZone.forEach(cityName => {
          const coords = cityCoordinates[cityName];
          if (coords) {
            const marker = new window.google.maps.Marker({
              position: coords,
              map: map,
              title: `${cityName} - Lubbock Zone ${zone.zone}`,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 5, // Smaller markers
                fillColor: color,
                fillOpacity: 0.9,
                strokeColor: '#000000',
                strokeWeight: 1
              }
            });

            // Add info window with zone-specific colors
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); color: ${color}; font-family: Arial, sans-serif; min-width: 180px; padding: 12px; border-radius: 6px; border: 2px solid ${color};">
                  <h4 style="margin: 0 0 8px 0; color: ${color}; font-size: 16px; font-weight: bold; text-align: center;">${cityName}</h4>
                  <p style="margin: 0 0 8px 0; font-size: 12px; text-align: center; color: white;">Lubbock Zone ${zone.zone}</p>
                  <div style="display: flex; justify-content: space-around; background: ${color}20; padding: 8px; border-radius: 4px;">
                    <div style="text-align: center;">
                      <div style="font-size: 10px; color: white;">Routine</div>
                      <div style="font-size: 14px; font-weight: bold; color: ${color};">${zone.routine}</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 10px; color: white;">Rush</div>
                      <div style="font-size: 14px; font-weight: bold; color: ${color};">${zone.rush}</div>
                    </div>
                  </div>
                </div>
              `
            });

            // Add click handler for info window
            marker.addListener('click', () => {
              // Close any open info windows
              if (window.currentInfoWindow) {
                window.currentInfoWindow.close();
              }
              infoWindow.open(map, marker);
              window.currentInfoWindow = infoWindow;
              
              // Also trigger search functionality
              if (onCityClick) {
                onCityClick(cityName);
              }
            });

            newMarkers.push(marker);
          }
        });
      });

      // Add Childress zone city markers (no circles)
      childressZones.forEach((zone, index) => {
        const color = getZoneColor(zone.color);

        // Add city markers for this zone
        const allCitiesInZone = getAllCitiesForZone('Childress', zone.zone);
        allCitiesInZone.forEach(cityName => {
          const coords = cityCoordinates[cityName];
          if (coords) {
            const marker = new window.google.maps.Marker({
              position: coords,
              map: map,
              title: `${cityName} - Childress Zone ${zone.zone}`,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 5, // Smaller markers
                fillColor: color,
                fillOpacity: 0.9,
                strokeColor: '#000000',
                strokeWeight: 1
              }
            });

            // Add info window with zone-specific colors
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); color: ${color}; font-family: Arial, sans-serif; min-width: 180px; padding: 12px; border-radius: 6px; border: 2px solid ${color};">
                  <h4 style="margin: 0 0 8px 0; color: ${color}; font-size: 16px; font-weight: bold; text-align: center;">${cityName}</h4>
                  <p style="margin: 0 0 8px 0; font-size: 12px; text-align: center; color: white;">Childress Zone ${zone.zone}</p>
                  <div style="display: flex; justify-content: space-around; background: ${color}20; padding: 8px; border-radius: 4px;">
                    <div style="text-align: center;">
                      <div style="font-size: 10px; color: white;">Routine</div>
                      <div style="font-size: 14px; font-weight: bold; color: ${color};">${zone.routine}</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 10px; color: white;">Rush</div>
                      <div style="font-size: 14px; font-weight: bold; color: ${color};">${zone.rush}</div>
                    </div>
                  </div>
                </div>
              `
            });

            // Add click handler for info window
            marker.addListener('click', () => {
              // Close any open info windows
              if (window.currentInfoWindow) {
                window.currentInfoWindow.close();
              }
              infoWindow.open(map, marker);
              window.currentInfoWindow = infoWindow;
              
              // Also trigger search functionality
              if (onCityClick) {
                onCityClick(cityName);
              }
            });

            newMarkers.push(marker);
          }
        });
      });

      // Add Hub markers (slightly larger than city markers)
      ['Lubbock', 'Childress'].forEach(hubName => {
        const coords = hubCoordinates[hubName];
        if (coords) {
          const marker = new window.google.maps.Marker({
            position: coords,
            map: map,
            title: `${hubName}`,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8, // Larger than city markers (which are 5)
              fillColor: '#dc2626',
              fillOpacity: 1,
              strokeColor: '#000000', // Remove white, use black border
              strokeWeight: 2
            },
            zIndex: 1000
          });

          // Add info window for hubs
          const hubPricing = hubName === 'Lubbock' ? 
            { routine: '$75', rush: '$100' } : 
            { routine: '$95', rush: '$125' };
          
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); color: #dc2626; font-family: Arial, sans-serif; min-width: 180px; padding: 12px; border-radius: 6px; border: 2px solid #dc2626;">
                <h4 style="margin: 0 0 8px 0; color: #dc2626; font-size: 16px; font-weight: bold; text-align: center;">${hubName}</h4>
                <p style="margin: 0 0 8px 0; font-size: 12px; text-align: center; color: white;">Service Hub</p>
                <div style="display: flex; justify-content: space-around; background: rgba(220, 38, 38, 0.1); padding: 8px; border-radius: 4px;">
                  <div style="text-align: center;">
                    <div style="font-size: 10px; color: white;">Routine</div>
                    <div style="font-size: 14px; font-weight: bold; color: #dc2626;">${hubPricing.routine}</div>
                  </div>
                  <div style="text-align: center;">
                    <div style="font-size: 10px; color: white;">Rush</div>
                    <div style="font-size: 14px; font-weight: bold; color: #dc2626;">${hubPricing.rush}</div>
                  </div>
                </div>
              </div>
            `
          });

          marker.addListener('click', () => {
            // Close any open info windows
            if (window.currentInfoWindow) {
              window.currentInfoWindow.close();
            }
            infoWindow.open(map, marker);
            window.currentInfoWindow = infoWindow;
          });

          newMarkers.push(marker);
        }
      });

      setMarkers(newMarkers);
      setZones(newZones);
    }, [map, lubbockZones, childressZones]);

    // Highlight searched city
    useEffect(() => {
      if (!map || !searchTerm || !window.google) return;

      const matchedCity = Object.keys(cityCoordinates).find(city =>
        city.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (matchedCity) {
        const coords = cityCoordinates[matchedCity];
        map.panTo(coords);
        map.setZoom(10);

        // Highlight the marker temporarily
        setTimeout(() => {
          map.setZoom(7);
          map.panTo({ lat: 33.8, lng: -101.2 });
        }, 3000);
      }
    }, [map, searchTerm]);

    return (
      <div className="gradient-outline p-6 reveal mt-8">
        <h3 className="text-xl font-bold text-[hsl(357,85%,52%)] mb-4 text-center">Visual Coverage Map</h3>
        <div 
          ref={mapRef} 
          className="w-full h-96 rounded-lg border border-white/20"
          style={{ minHeight: '400px' }}
        />
        <div className="mt-4 text-center">
          <p className="text-white/70 text-sm mb-2">Click any city marker to search for pricing</p>
          <div className="flex justify-center items-center gap-4 text-xs text-white/60">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-600 rounded-full"></div>
              <span>Zone 1</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Zone 2</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <span>Zone 3</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-300 rounded-full"></div>
              <span>Zone 4</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-200 rounded-full"></div>
              <span>Zone 5</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 rounded-full"></div>
              <span>Zone 6</span>
            </div>
          </div>
        </div>
      </div>
    );
  };



  // Enhanced Zone Card with all cities displayed (counties removed)
  const ZoneCard = ({ zone, hub, index }) => {
    // Function to chunk cities into balanced rows
    const chunkCities = (cities) => {
      const count = cities.length;
      if (count <= 2) return [cities];
      if (count === 3) return [cities];
      if (count === 4) return [cities.slice(0, 2), cities.slice(2, 4)];
      if (count === 5) return [cities.slice(0, 3), cities.slice(3, 5)];
      if (count === 6) return [cities.slice(0, 3), cities.slice(3, 6)];
      return [cities]; // fallback
    };

    const cityRows = chunkCities(zone.cities);

    return (
      <div className="gradient-outline p-6 reveal text-center" style={{ transitionDelay: `${0.1 + index * 0.05}s` }}>
        <div className="flex items-center justify-center mb-4">
          <div className={`w-4 h-4 ${zone.color} rounded mr-3`}></div>
          <h4 className="text-lg font-semibold text-white">Zone {zone.zone}</h4>
        </div>
        
        <div className="mb-4">
          <h5 className="text-sm font-semibold text-white/90 mb-3">Major Cities:</h5>
          <div className="space-y-2">
            {cityRows.map((row, rowIndex) => (
              <div key={rowIndex} className="flex flex-wrap gap-1 justify-center">
                {row.map(city => (
                  <span key={city} className="text-xs bg-[hsl(357,85%,52%)]/20 text-white px-2 py-1 rounded border border-[hsl(357,85%,52%)]/40">
                    {city}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex justify-between items-center pt-4 border-t border-white/20">
          <div className="text-center flex-1">
            <div className="text-sm text-white/70">Routine</div>
            <div className="text-lg font-bold text-[hsl(357,85%,52%)]">{zone.routine}</div>
          </div>
          <div className="text-center flex-1">
            <div className="text-sm text-white/70">Rush</div>
            <div className="text-lg font-bold text-[hsl(357,85%,52%)]">{zone.rush}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="black-section">
      <Container className="py-16">
        <div className="text-center mb-12">
          <h1 className="heading-accent font-serif-head text-3xl font-bold text-white">Service Areas & Pricing</h1>
          <div className="my-8 max-w-7xl mx-auto"><Divider /></div>
          <p className="text-lg text-white/90 mb-6">
            WesTex Legal provides comprehensive process serving across Texas and New Mexico with transparent, mileage-based zone pricing.
          </p>
        </div>

        {/* Interactive Tab Navigation */}
        <div className="mb-12">
          <div className="flex justify-center mb-8">
            <div className="bg-white/10 rounded-lg p-1 backdrop-blur-sm border border-white/20">
              <button
                onClick={() => setActiveTab('zones')}
                className={`px-6 py-3 rounded-md transition-all duration-200 text-sm font-semibold ${
                  activeTab === 'zones' 
                    ? 'bg-[hsl(357,85%,52%)] text-white shadow-lg' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Zone Cards
              </button>
              <button
                onClick={() => setActiveTab('calculator')}
                className={`px-6 py-3 rounded-md transition-all duration-200 text-sm font-semibold ${
                  activeTab === 'calculator' 
                    ? 'bg-[hsl(357,85%,52%)] text-white shadow-lg' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Coverage Calculator
              </button>
            </div>
          </div>

          {/* Search Direction Message */}
          <div className="mb-8 text-center">
            <div className="inline-block bg-[hsl(357,85%,52%)]/10 border border-[hsl(357,85%,52%)]/30 rounded-lg px-6 py-4">
              <p className="text-white/90 mb-2">
                <span className="font-semibold">Don't see your city?</span>
              </p>
              <p className="text-white/70 text-sm">
                Use our <span className="text-[hsl(357,85%,52%)] font-semibold">Coverage Calculator</span> tab above to search for your location and get instant pricing.
              </p>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'zones' && (
            <>
              {/* Texas Coverage & Pricing */}
              <div className="mb-16">
                
                {/* Lubbock Coverage */}
                <div className="mb-12">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-[hsl(357,85%,52%)] mb-2">Lubbock</h3>
                    <p className="text-white/80">Serving West Texas</p>
                  </div>
                  
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
                    {lubbockZones.map((zone, index) => (
                      <ZoneCard key={zone.zone} zone={zone} hub="Lubbock" index={index} />
                    ))}
                  </div>
                </div>

                {/* Childress Coverage */}
                <div className="mb-12">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-[hsl(357,85%,52%)] mb-2">Childress</h3>
                    <p className="text-white/80">Serving North Texas Panhandle</p>
                  </div>
                  
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
                    <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {childressZones.slice(0, 3).map((zone, index) => (
                        <ZoneCard key={zone.zone} zone={zone} hub="Childress" index={index} />
                      ))}
                    </div>
                    <div className="lg:col-span-3 flex justify-center">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                        {childressZones.slice(3).map((zone, index) => (
                          <ZoneCard key={zone.zone} zone={zone} hub="Childress" index={index + 3} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* New Mexico Coverage & Pricing */}
              <div className="mb-16">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-[hsl(357,85%,52%)] mb-2">New Mexico</h3>
                  <p className="text-white/80">Statewide flat rate coverage</p>
                </div>
                
                <div className="max-w-md mx-auto">
                  <div className="gradient-outline p-6 text-center reveal">
                    <h4 className="text-xl font-bold text-white mb-4">Statewide Coverage</h4>
                    <p className="text-white/80 mb-6">We serve all 33 counties in New Mexico</p>
                    <div className="flex justify-between text-lg mb-4">
                      <span className="text-white/70">Routine: <span className="text-[hsl(357,85%,52%)] font-bold text-xl">$125</span></span>
                      <span className="text-white/70">Rush: <span className="text-[hsl(357,85%,52%)] font-bold text-xl">$175</span></span>
                    </div>
                    <p className="text-white/70 text-sm">Statewide coverage • Per address</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'calculator' && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-white text-center mb-8">Coverage Calculator</h2>
              <CoverageCalculator />
            </div>
          )}
        </div>

        {/* Additional Services */}
        <div className="border-t border-white/20 pt-12">
          <h2 className="text-3xl font-bold text-[hsl(357,85%,52%)] text-center mb-8">Additional Services</h2>
          
          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            {/* US Network */}
            <div className="gradient-outline p-6 reveal text-center">
              <div className="flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-[hsl(357,85%,52%)] mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm9 8c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm7-8c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zM9 4c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z"/>
                  <path stroke="currentColor" strokeWidth="1" fill="none" d="M5 12h6M11 4v6M13 10l6 2M19 14l-6 2M13 14v6M11 18H5"/>
                </svg>
                <h3 className="text-lg font-semibold text-white">Nationwide Network</h3>
              </div>
              <p className="text-white/80 mb-4">
                Through our robust network of professional process servers, we can service anywhere in the United States.
              </p>
              <div className="text-sm text-white/70">
                Custom quotes provided based on location and requirements
              </div>
            </div>

            {/* Hague Convention */}
            <div className="gradient-outline p-6 reveal text-center">
              <div className="flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-[hsl(357,85%,52%)] mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                <h3 className="text-lg font-semibold text-white">International Service</h3>
              </div>
              <p className="text-white/80 mb-4">
                We have experience with Hague Convention services for international process serving requirements.
              </p>
              <div className="text-sm text-white/70">
                Specialized service • Custom consultation required
              </div>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-[hsl(357,85%,52%)]/20 border border-[hsl(357,85%,52%)]/40 rounded-lg px-6 py-4">
            <p className="text-white font-semibold">
              All prices listed are on a per address basis.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/contact" variant="outline">Get Custom Quote</Button>
            <Button href="tel:8064011749">Call (806) 401-1749</Button>
          </div>
        </div>
      </Container>
    </section>
  );
};

function App() {
  useRevealOnScroll();
  
  // Initialize Google Analytics
  useEffect(() => {
    initializeGA();
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
          <PortalV2LegacyRedirect />
          <Routes>
            {/* ========= PORTAL V2 (canonical, lives at /portal/*) ========= */}
            <Route path="/portal/login" element={<PortalV2Login />} />
            <Route path="/portal/register" element={<PortalV2Register />} />
            <Route path="/portal/reset-password" element={<PortalV2ResetPassword />} />
            <Route path="/portal/pay/:token" element={<ClientV2GuestPayment />} />

            {/* Client */}
            <Route path="/portal/dashboard" element={<ProtectedRoute><ClientV2Dashboard /></ProtectedRoute>} />
            <Route path="/portal/jobs" element={<ProtectedRoute><ClientV2Jobs /></ProtectedRoute>} />
            <Route path="/portal/jobs/:jobId" element={<ProtectedRoute><JobV2Detail /></ProtectedRoute>} />
            <Route path="/portal/activity" element={<ProtectedRoute><ClientV2Activity /></ProtectedRoute>} />
            <Route path="/portal/submit" element={<ProtectedRoute><ClientV2SubmitJob /></ProtectedRoute>} />
            <Route path="/portal/invoices" element={<ProtectedRoute><ClientV2Invoices /></ProtectedRoute>} />
            <Route path="/portal/invoices/:invoiceId" element={<ProtectedRoute><ClientV2InvoiceDetail /></ProtectedRoute>} />
            <Route path="/portal/settings" element={<ProtectedRoute><ClientV2Settings /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/portal/admin" element={<ProtectedRoute adminOnly><AdminV2Dashboard /></ProtectedRoute>} />
            <Route path="/portal/admin/jobs" element={<ProtectedRoute adminOnly><AdminV2Jobs /></ProtectedRoute>} />
            <Route path="/portal/admin/server/:serverName" element={<ProtectedRoute adminOnly><AdminV2ServerJobs /></ProtectedRoute>} />
            <Route path="/portal/admin/jobs-map" element={<ProtectedRoute adminOnly><AdminV2JobsMap /></ProtectedRoute>} />
            <Route path="/portal/admin/invoices" element={<ProtectedRoute adminOnly><AdminV2Invoices /></ProtectedRoute>} />
            <Route path="/portal/admin/clients" element={<ProtectedRoute adminOnly><AdminV2Clients /></ProtectedRoute>} />
            <Route path="/portal/admin/domains" element={<ProtectedRoute adminOnly><AdminV2Domains /></ProtectedRoute>} />
            <Route path="/portal/admin/referrals" element={<ProtectedRoute adminOnly><AdminV2Referrals /></ProtectedRoute>} />
            <Route path="/portal/admin/notary" element={<ProtectedRoute adminOnly><AdminV2NotaryBookings /></ProtectedRoute>} />
            <Route path="/portal/admin/server-finder" element={<ProtectedRoute adminOnly><AdminV2ServerFinder /></ProtectedRoute>} />
            {/* ============================================================ */}

            {/* Catch-all under /portal/* falls through to V2 dashboard via 404 */}
            <Route path="/portal/*" element={
              <ProtectedRoute>
                <ClientV2Dashboard />
              </ProtectedRoute>
            } />
            
            {/* Guest Payment Route - No Auth Required */}
            <Route path="/pay/:token" element={<ClientV2GuestPayment />} />
            <Route path="/pay-statement/:token" element={<ClientV2StatementPayment />} />

            {/* V2 Preview Site — parallel design, original left untouched */}
            <Route path="/v2/*" element={<V2Routes />} />

            {/* V2-themed standalone routes (must be outside V1 NavBar wrapper) */}
            <Route path="/job-request" element={<V2JobRequest />} />
            <Route path="/job-request-success" element={<V2JobRequestSuccess />} />

            {/* =========  V2 IS NOW CANONICAL  =========
                Root paths (/, /about, /services, /areas-pricing, /notary, /faq, /contact)
                render the V2 editorial design. V1 is preserved under /legacy/* for archival. */}
            <Route path="/" element={<V2Home />} />
            <Route path="/about" element={<V2About />} />
            <Route path="/about-us" element={<AboutRedirect />} />
            <Route path="/services" element={<V2Services />} />
            <Route path="/areas-pricing" element={<V2AreasPricing />} />
            <Route path="/areas-covered" element={<V2AreasPricing />} />
            <Route path="/notary" element={<V2Notary />} />
            <Route path="/faq" element={<V2FAQ />} />
            <Route path="/contact" element={<V2Contact />} />
            <Route path="/privacy" element={<V2Privacy />} />
            <Route path="/terms" element={<V2Terms />} />

            {/* Main Site Routes */}
            {/* Standalone notary pages - no header/hero */}
            <Route path="/notary/confirmation" element={<NotaryConfirmation />} />
            <Route path="/notary/manage" element={<NotaryManageBooking />} />

            {/* Utility/redirect routes (no chrome) */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact-us" element={<ContactRedirect />} />
            <Route path="/notaryty" element={<NotFoundRedirect />} />
            <Route path="/payment-success" element={<V2PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />

            {/* Legacy V1 pages preserved under /legacy/* with V1 chrome */}
            <Route path="/legacy/*" element={
              <>
                <NavBar />
                <Hero />
                <Page>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/areas-pricing" element={<AreasCovered />} />
                    <Route path="/notary" element={<Notary />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/contact" element={<Contact />} />
                  </Routes>
                </Page>
                <Footer />
                <StickyMobileCallButton />
              </>
            } />

            {/* Legacy job-request + notary-new routes retained for old links */}
            <Route path="/job-request-legacy" element={
              <><NavBar /><Hero /><Page><JobRequestNew /></Page><Footer /><StickyMobileCallButton /></>
            } />
            <Route path="/job-request-success-legacy" element={
              <><NavBar /><Hero /><Page><JobRequestSuccess /></Page><Footer /><StickyMobileCallButton /></>
            } />
            <Route path="/notary-new" element={<NotaryBooking />} />
            <Route path="/notary-new/confirmation" element={<NotaryConfirmation />} />

            {/* V2-styled 404 — catch-all fallback for any unmatched URL */}
            <Route path="*" element={<V2NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

const Pricing = () => {
  // Redirect to the combined Areas & Pricing page using React Router
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/areas-pricing', { replace: true });
  }, [navigate]);
  
  return null;
};

// Redirect old contact-us URL to contact
const ContactRedirect = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/contact', { replace: true });
  }, [navigate]);
  
  return null;
};

// Redirect dead /about-us URL to the canonical /about page
const AboutRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/about', { replace: true });
  }, [navigate]);
  return null;
};

// Handle old notaryty URL
const NotFoundRedirect = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/notary', { replace: true });
  }, [navigate]);
  
  return null;
};

// Payment Success/Cancelled Pages
const PaymentSuccess = () => {
  const [sessionId, setSessionId] = useState('');
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const session_id = urlParams.get('session_id');
    
    if (session_id) {
      setSessionId(session_id);
      checkPaymentStatus(session_id);
    } else {
      setStatus('error');
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10; // Increased from 5 to 10
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    // Add initial delay for first attempt to let Stripe process
    if (attempts === 0) {
      await new Promise(resolve => setTimeout(resolve, 3000)); // 3 second delay
    }

    try {
      const response = await axios.get(`/api/payments/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentDetails(data);
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Don't immediately show error on first few attempts - Stripe might be processing
      if (attempts < 3) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <section className="black-section min-h-screen">
      <Container className="py-16 text-center">
        <div className="max-w-2xl mx-auto">
          {status === 'checking' && (
            <div className="gradient-outline p-8">
              <div className="animate-spin w-12 h-12 border-4 border-[hsl(357,85%,52%)] border-t-transparent rounded-full mx-auto mb-4"></div>
              <h1 className="text-2xl font-bold text-white mb-4">Processing Payment...</h1>
              <p className="text-white/80">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="gradient-outline p-8">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Payment Successful!</h1>
              <p className="text-white/80 mb-6">Thank you for your payment. Your transaction has been completed successfully.</p>
              {paymentDetails && (
                <div className="bg-white/10 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-white mb-2">Payment Confirmation:</h3>
                  <p className="text-white/80 text-sm">Job Reference: {paymentDetails.metadata?.reference_id || 'N/A'}</p>
                  <p className="text-white/80 text-sm">Amount Paid: ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}</p>
                  <p className="text-white/80 text-sm">Status: Payment Successful</p>
                </div>
              )}
              <Button to="/">Return to Homepage</Button>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="gradient-outline p-8">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Payment Issue</h1>
              <p className="text-white/80 mb-6">
                {status === 'expired' && "Your payment session has expired."}
                {status === 'timeout' && "Payment verification timed out. Please check your email for confirmation."}
                {status === 'error' && "There was an issue processing your payment."}
              </p>
              <div className="flex gap-4 justify-center">
                <Button to="/contact">Contact Us</Button>
                <Button to="/" variant="outline">Return to Homepage</Button>
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

const PaymentCancelled = () => {
  return (
    <section className="black-section min-h-screen">
      <Container className="py-16 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="gradient-outline p-8">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Payment Cancelled</h1>
            <p className="text-white/80 mb-6">You cancelled the payment process. No charges have been made.</p>
            <div className="flex gap-4 justify-center">
              <Button to="/contact">Contact Us</Button>
              <Button to="/" variant="outline">Return to Homepage</Button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default App;